import { Injectable } from '@angular/core';
import { Observable, from, map, firstValueFrom, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { Router } from '@angular/router';
import { AvailableApplication } from './environment.interface';
import { DataService } from './data.service';
import { SignupSource, SignupSourceUtil, Utils } from './utils';
import {
  EntraAuthService,
  EntraFlowResult,
  SocialProvider,
} from './entra-auth.service';
import { CustomAuthAccountData } from '@azure/msal-browser/custom-auth';
import { GoogleLinkService } from './google-link.service';
import {
  ENTRA_NATIVE_CLIENT_ID,
  FEDERATED_EMAIL_OTP_ROUTING_ENABLED,
  ROUTE_EMAIL_BY_SIGN_UP_AUTH_METHOD,
  MICROSOFT_GRAPH_AUDIENCE,
  POST_LOGIN_GOOGLE_LINK_ENABLED,
  useGoogleEmailOtpIdp,
  useFirebasePasswordlessEmail,
  useNumericGoogleEmailOtp,
} from '../config/auth-config';
import { GoogleFirebaseAuthService } from './google-firebase-auth.service';
import {
  logGoogleFlow,
  logGoogleFlowRedirect,
} from '../utils/google-auth-flow-log';
import { resolvePasswordlessEmailContinueUrl } from '../utils/passwordless-email-inbox-sync';
import {
  buildCrossAppRelayReturnUrl,
  resolveFinalLogoutLandingUrl,
} from '../utils/cross-app-logout';
import {
  ayaAppUrlForTier,
  resolveAyaDeployTierFromReturnUrl,
  resolveAutomateAppUrl,
  resolveCustodianAppUrl,
  resolveHealthpayAppUrl,
  resolveThreadsAppUrl,
  wwwAppUrlForTier,
  type AyaDeployTier,
} from '../utils/aya-deploy-urls';
import { isGoogleBrowserLogoutUrl } from '../utils/google-browser-logout';
import { getFirebaseRedirectBootstrapState } from 'src/firebase-social-bootstrap';
import {
  clearHandoffOrganization as clearStoredHandoffOrganization,
  getHandoffOrganization as readHandoffOrganization,
  resolveActiveOrganizationId as readActiveOrganizationId,
  setHandoffOrganization as storeHandoffOrganization,
} from '../utils/active-organization.util';
import { isGeofenceDeniedError as isGeofenceDeniedApiError } from '../utils/geofence-error.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly FIREBASE_SOCIAL_PROVIDER_KEY =
    'he_firebase_social_provider';

  userCompanies: any[] = [];
  accessToken: string = '';
  /** Validated deep-link return URL; honored after successful auth. */
  deepLinkReturnUrl: string | null = null;
  /**
   * Actual Entra flow in use after seamless login/signup fallback
   * (login with unknown email → signup, signup with existing email → login).
   */
  effectiveAuthMode: 'login' | 'signup' = 'login';
  /** Passed to identity/register on first Accounts provision (email vs social). */
  private registerSignUpAuthMethod: 'email' | 'google' | 'apple' = 'email';
  /** Marketing page origin for first-time signup (e.g. providers landing). */
  private registerSignupSource: SignupSource | null = null;
  /** Last successful Accounts identity/register response (user + organizations). */
  lastAccountsIdentity: unknown = null;
  /** True while enabling SMS MFA from the signed-in profile (phone + SMS verify). */
  smsMfaEnableInProgress = false;
  /** Phone captured during SMS MFA enable; submitted after email verification. */
  pendingSmsMfaEnablePhone = '';
  /** Server-side Entra OTP flow (avoids MSAL redirect popup on initiate). */
  private entraEmailFlowToken: string | null = null;
  private entraEmailFlowKind: 'login' | 'register' | null = null;
  /** BFF entra/start + resend: retry custom extension timeouts (AADSTS1100001). */
  private readonly entraOtpStartMaxAttempts = 4;
  private readonly entraOtpStartBaseDelayMs = 400;
  /** Refresh this many milliseconds before JWT/local expiry. */
  private readonly tokenRefreshBufferMs = 5 * 60 * 1000;
  /** Proactive background refresh interval while the app is open. */
  private readonly sessionRefreshIntervalMs = 5 * 60 * 1000;
  private readonly logoutClearMaxAttempts = 8;
  private readonly logoutClearDelayMs = 250;
  private refreshSessionPromise: Promise<string | null> | null = null;
  private sessionRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly signedOutSessionKey = 'he.accounts.signedOut';
  private readonly signedOutLocalStorageKey = 'he.accounts.signedOut';
  private readonly pendingAyaReturnUrlKey = 'he_aya_pending_return_url';
  /** User chose email link on the OTP inbox screen (Twilio/ACS primary). */
  private googlePasswordlessLinkSession = false;

  /** Email OTP via Accounts BFF vs MSAL Custom Auth + `/api` Entra proxy. */
  private useEntraEmailOtpBff(): boolean {
    return environment.entraEmailOtpViaBff === true;
  }

  /** Google Identity Platform email OTP via Accounts BFF + Firebase session. */
  private useGoogleEmailOtp(): boolean {
    return useGoogleEmailOtpIdp();
  }

  /** Firebase passwordless email link (no ACS numeric OTP). */
  private useFirebasePasswordlessEmailAuth(): boolean {
    return useGoogleEmailOtpIdp() && useFirebasePasswordlessEmail();
  }

  /** Numeric email OTP via Accounts BFF (ACS or Twilio Verify). */
  private useNumericGoogleEmailOtpAuth(): boolean {
    return useGoogleEmailOtpIdp() && useNumericGoogleEmailOtp();
  }

  /** Global or per-session Firebase passwordless email link delivery. */
  private useGooglePasswordlessLinkFlow(): boolean {
    return this.useFirebasePasswordlessEmailAuth() || this.googlePasswordlessLinkSession;
  }

  /** Twilio/ACS is primary but user may opt into a passwordless email link. */
  canOfferGooglePasswordlessLinkFallback(): boolean {
    return this.useNumericGoogleEmailOtpAuth();
  }

  clearGooglePasswordlessLinkSession(): void {
    this.googlePasswordlessLinkSession = false;
  }

  switchToGooglePasswordlessEmailLink(
    email: string
  ): Observable<LoginResponse | ValidOTPResponse> {
    return from(this.requestGooglePasswordlessEmailLink(email));
  }

  isFirebaseEmailLinkSignInReturn(): boolean {
    return (
      this.useGoogleEmailOtp() &&
      this._googleFirebaseAuth.isEmailLinkSignInReturn()
    );
  }

  hasResolvedEmailForSignInLink(): boolean {
    return !!(
      this._googleFirebaseAuth.readEmailFromSignInLinkUrl() ||
      this._googleFirebaseAuth.readStoredEmailForSignInLink()
    );
  }

  resolveEmailForSignInLink(): string {
    return (
      this._googleFirebaseAuth.readEmailFromSignInLinkUrl() ||
      this._googleFirebaseAuth.readStoredEmailForSignInLink() ||
      ''
    );
  }

  constructor(
    private readonly _localStorageService: LocalStorageService,
    private readonly _router: Router,
    private readonly _dataService: DataService,
    private readonly _entraAuthService: EntraAuthService,
    private readonly _googleLinkService: GoogleLinkService,
    private readonly _googleFirebaseAuth: GoogleFirebaseAuthService
  ) {}

  private static readonly postAuthAppSessionKey = 'he_auth_app';
  private static readonly signupSourceSessionKey = 'he_auth_signup_source';

  /** Capture and validate an optional deep-link returnUrl query value. */
  captureDeepLinkReturnUrl(rawReturnUrl: string | null | undefined) {
    if (!rawReturnUrl) {
      return;
    }
    const sanitized = Utils.sanitizeReturnUrl(String(rawReturnUrl).trim());
    if (sanitized) {
      this.deepLinkReturnUrl = sanitized;
      try {
        sessionStorage.setItem('he_auth_return_url', sanitized);
      } catch {
        // ignore
      }
    }
  }

  /** Capture marketing signup origin from deep link (?signupSource=providers). */
  captureDeepLinkSignupSource(rawSignupSource: string | null | undefined) {
    const sanitized = SignupSourceUtil.sanitize(rawSignupSource);
    if (!sanitized) {
      return;
    }
    this.registerSignupSource = sanitized;
    try {
      sessionStorage.setItem(AuthService.signupSourceSessionKey, sanitized);
    } catch {
      // ignore
    }
  }

  resolveRegisterSignupSource(): SignupSource | null {
    if (this.registerSignupSource) {
      return this.registerSignupSource;
    }
    try {
      return SignupSourceUtil.sanitize(
        sessionStorage.getItem(AuthService.signupSourceSessionKey)
      );
    } catch {
      return null;
    }
  }

  consumeRegisterSignupSource(): void {
    this.registerSignupSource = null;
    try {
      sessionStorage.removeItem(AuthService.signupSourceSessionKey);
    } catch {
      // ignore
    }
  }

  /** Persist ?app= (e.g. clinic) across OAuth redirects that strip query params. */
  capturePostAuthApp(rawApp: string | null | undefined): void {
    if (!rawApp) {
      return;
    }
    const normalized = String(rawApp).trim().toLowerCase();
    if (!normalized) {
      return;
    }
    if (
      !Object.values(AvailableApplication).includes(
        normalized as AvailableApplication
      )
    ) {
      return;
    }
    try {
      sessionStorage.setItem(AuthService.postAuthAppSessionKey, normalized);
    } catch {
      // ignore
    }
  }

  resolvePostAuthApp(): string | null {
    try {
      return sessionStorage.getItem(AuthService.postAuthAppSessionKey);
    } catch {
      return null;
    }
  }

  consumePostAuthApp(): void {
    try {
      sessionStorage.removeItem(AuthService.postAuthAppSessionKey);
    } catch {
      // ignore
    }
  }

  /** Product-app entry via ?app= — stay on auth portal; do not relay to www/Aya. */
  redirectsWithoutProductLicense(app: string | null | undefined): boolean {
    const normalized = String(app || '').trim().toLowerCase();
    return (
      normalized === AvailableApplication.Admin ||
      normalized === AvailableApplication.Clinic ||
      normalized === AvailableApplication.HealthPay ||
      normalized === AvailableApplication.Automate ||
      normalized === AvailableApplication.Custodian ||
      normalized === AvailableApplication.Threads ||
      normalized === AvailableApplication.Studies
    );
  }

  /**
   * When admin/clinic sends the user to dev-auth (?app=admin|clinic), clear logout
   * gates and stale Aya return URLs so post-login goes to the product app, not www.
   */
  prepareProductAppLoginEntry(app: string | null | undefined): void {
    if (!this.redirectsWithoutProductLicense(app)) {
      return;
    }
    this.clearSignedOutLocally();
    this.deepLinkReturnUrl = null;
    try {
      sessionStorage.removeItem('he_auth_return_url');
    } catch {
      // ignore
    }
    this.capturePostAuthApp(app);
  }

  /**
   * When license lookup fails after login, still honor ?app=admin|clinic.
   * Returns true when a redirect was started.
   */
  tryPostAuthAppFallback(
    token: AuthToken,
    appQueryParam: string | null | undefined
  ): boolean {
    if (!appQueryParam || !this.redirectsWithoutProductLicense(appQueryParam)) {
      return false;
    }
    this.appBasedRedirection([], token, appQueryParam);
    return true;
  }

  /** Navigate to a product app’s /app-redirect handoff (e.g. admin → localhost:4202). */
  redirectToPostAuthApp(app: AvailableApplication | string): void {
    const normalized = String(app).trim().toLowerCase() as AvailableApplication;
    void this.redirectToUrlWithTokenPrepared(this.getUrlByAppName(normalized, true));
    this.consumePostAuthApp();
  }

  /** Whether to call resolve-email before Entra OTP and route google/apple users. */
  shouldRouteEmailBySignUpAuthMethod(): boolean {
    return ROUTE_EMAIL_BY_SIGN_UP_AUTH_METHOD && !this.useGoogleEmailOtp();
  }

  login(
    email: string,
    type: 'SMS' | undefined = undefined,
    options?: { maxAttempts?: number }
  ): Observable<LoginResponse | ValidOTPResponse> {
    this.registerSignUpAuthMethod = 'email';
    if (this.useFirebasePasswordlessEmailAuth()) {
      return from(this.startGooglePasswordlessEmailLink(email, true, 'login'));
    }
    if (this.useGoogleEmailOtp()) {
      return from(this.startGoogleSignInViaBff(email, true));
    }
    const maxAttempts = Math.max(
      1,
      options?.maxAttempts ?? this.entraOtpStartMaxAttempts
    );
    if (maxAttempts <= 1) {
      return from(this.startEntraSignIn(email, type));
    }
    return from(this.startEntraSignInWithRetries(email, type, maxAttempts));
  }

  signUp(
    email: string,
    options?: { maxAttempts?: number }
  ): Observable<LoginResponse | ValidOTPResponse> {
    this.registerSignUpAuthMethod = 'email';
    if (this.useFirebasePasswordlessEmailAuth()) {
      return from(this.startGooglePasswordlessEmailLink(email, true, 'signup'));
    }
    if (this.useGoogleEmailOtp()) {
      return from(this.startGoogleSignUpViaBff(email, true));
    }
    const maxAttempts = Math.max(
      1,
      options?.maxAttempts ?? this.entraOtpStartMaxAttempts
    );
    if (maxAttempts <= 1) {
      return from(this.startEntraSignUp(email));
    }
    return from(this.startEntraSignUpWithRetries(email, maxAttempts));
  }

  validateOTP(
    otp: string,
    email: string,
    skip2fa: boolean = false
  ): Observable<ValidOTPResponse | LoginResponse> {
    if (this.useNumericGoogleEmailOtpAuth()) {
      return from(this.continueGoogleWithCodeViaBff(otp, email));
    }
    return from(this.continueEntraWithCode(otp, email));
  }

  registerStrongAuthPhone(
    phone: string,
    email: string
  ): Observable<ValidOTPResponse | LoginResponse> {
    return from(this.continueEntraRegisterPhone(phone, email));
  }

  submitPassword(
    password: string,
    email: string,
    mode: 'login' | 'signup' = 'login'
  ): Observable<ValidOTPResponse | LoginResponse> {
    if (mode === 'signup') {
      return from(this.continueEntraSignUpPassword(password, email));
    }
    return from(this.continueEntraWithPassword(password, email));
  }

  /** Wait until MSAL Custom Auth client is initialized (required before loginRedirect). */
  async ensureMsalReady(): Promise<void> {
    await this._entraAuthService.getClient();
    this._entraAuthService.clearStuckInteraction();
  }

  /** Finish processing Entra loginRedirect when ?code= is on /login or /redirect. */
  async ensureMsalRedirectHandled(): Promise<void> {
    await this.ensureMsalReady();
    await this._entraAuthService.awaitRedirectResponse();
  }

  /**
   * Clear stale logout / MSAL state before a Lovable ?provider=google|apple deep-link.
   */
  consumeEntraRedirectError(): string | null {
    return this._entraAuthService.consumeRedirectError();
  }

  consumeSocialOAuthAbortedFlag(): boolean {
    return this._entraAuthService.consumeSocialOAuthAbortedFlag();
  }

  markSocialOAuthAborted(): void {
    this._entraAuthService.markSocialOAuthAborted();
  }

  clearSocialOAuthAbortedFlag(): void {
    this._entraAuthService.clearSocialOAuthAbortedFlag();
  }

  peekOAuthFailureInBrowser(): boolean {
    return this._entraAuthService.peekOAuthFailureInBrowser();
  }

  async prepareForSocialDeepLink(): Promise<void> {
    this.clearSessionStorageForNewSocialLogin();
    this.clearSignedOutLocally();
    this.stopPeriodicSessionRefresh();
    this.refreshSessionPromise = null;
    await this._entraAuthService.signOut();
    this.clearAuthFromLocalStorage();
  }

  private readonly emailDeepLinkPendingKey = 'he_email_deep_link_pending';

  /** Keep Lovable ?email= intent if Entra hosted sign-out reloads /login without query. */
  persistEmailDeepLinkIntent(
    email: string,
    returnUrl: unknown,
    signupSource?: unknown
  ): void {
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      return;
    }
    const sanitizedReturn = returnUrl
      ? Utils.sanitizeReturnUrl(String(returnUrl).trim())
      : null;
    const sanitizedSignupSource = SignupSourceUtil.sanitize(
      signupSource != null ? String(signupSource) : null
    );
    try {
      sessionStorage.setItem(
        this.emailDeepLinkPendingKey,
        JSON.stringify({
          email: normalized,
          returnUrl: sanitizedReturn,
          signupSource: sanitizedSignupSource,
        })
      );
    } catch {
      // ignore
    }
  }

  peekEmailDeepLinkIntent(): {
    email: string;
    returnUrl: string | null;
    signupSource: SignupSource | null;
  } | null {
    try {
      const raw = sessionStorage.getItem(this.emailDeepLinkPendingKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as {
        email?: string;
        returnUrl?: string | null;
        signupSource?: string | null;
      };
      const email = (parsed.email || '').trim().toLowerCase();
      if (!email) {
        return null;
      }
      return {
        email,
        returnUrl: parsed.returnUrl ? String(parsed.returnUrl) : null,
        signupSource: SignupSourceUtil.sanitize(parsed.signupSource),
      };
    } catch {
      return null;
    }
  }

  clearEmailDeepLinkIntent(): void {
    try {
      sessionStorage.removeItem(this.emailDeepLinkPendingKey);
    } catch {
      // ignore
    }
  }

  /** After ?email= session teardown, allow resolve-email + OTP/social to proceed. */
  releaseSignedOutGateForLogin(): void {
    this.clearSignedOutLocally();
  }

  stripOAuthParamsForEmailDeepLink(): void {
    this._entraAuthService.stripOAuthParamsFromBrowserUrl();
  }

  /**
   * Revokes Entra server-side when possible, clears MSAL + Accounts storage.
   * Preserves deepLinkReturnUrl — call captureDeepLinkReturnUrl first if needed.
   */
  async prepareForEmailDeepLink(
    email?: string,
    returnUrlHint?: unknown
  ): Promise<void> {
    if (email?.trim()) {
      this.persistEmailDeepLinkIntent(
        email,
        returnUrlHint ?? this.deepLinkReturnUrl,
        this.resolveRegisterSignupSource()
      );
    }

    const preservedReturnUrl = this.deepLinkReturnUrl;
    const preservedSignupSource = this.registerSignupSource;
    let sessionReturnUrl: string | null = null;
    try {
      sessionReturnUrl = sessionStorage.getItem('he_auth_return_url');
    } catch {
      // ignore
    }

    this.clearEntraBffFlow();
    this.clearStaleSocialRedirectPending();
    this.stopPeriodicSessionRefresh();
    this.refreshSessionPromise = null;

    const token =
      this._localStorageService.authorizationData?.token?.trim() || '';
    if (token) {
      try {
        await firstValueFrom(
          this._dataService.post(
            'api/v1/auth/identity/logout-everywhere',
            {},
            token
          )
        );
      } catch (err) {
        console.warn(
          '[auth] logout-everywhere before ?email= deep-link failed',
          err
        );
      }
    }

    await this.clearSessionUntilSignedOut({ localOnly: true });
    this._entraAuthService.clearStuckInteraction();
    this.clearSignedOutLocally();

    const hintSanitized =
      returnUrlHint != null && String(returnUrlHint).trim()
        ? Utils.sanitizeReturnUrl(String(returnUrlHint).trim())
        : null;
    const returnUrl = hintSanitized || preservedReturnUrl || sessionReturnUrl;
    if (returnUrl) {
      this.captureDeepLinkReturnUrl(returnUrl);
      try {
        sessionStorage.setItem('he_auth_return_url', returnUrl);
      } catch {
        // ignore
      }
    }
    if (preservedSignupSource) {
      this.captureDeepLinkSignupSource(preservedSignupSource);
    }
  }

  private bffSignupSourcePayload(): { signupSource?: SignupSource } {
    const signupSource = this.resolveRegisterSignupSource();
    return signupSource ? { signupSource } : {};
  }

  /**
   * Google / Apple via full-page loginRedirect (Lovable deep-link).
   * Firebase redirect when emailOtpIdp is google; Entra MSAL otherwise.
   */
  startSocialRedirect(provider: SocialProvider): Observable<void> {
    if (this.useGoogleEmailOtp()) {
      return from(this._googleFirebaseAuth.signInWithSocialRedirect(provider));
    }
    return from(this._entraAuthService.signInWithSocialRedirect(provider));
  }

  /** True when the URL looks like a Firebase OAuth redirect return. */
  isFirebaseSocialRedirectReturn(): boolean {
    if (this.isReturningFromFirebaseSocialRedirect()) {
      return true;
    }
    return (
      this.useGoogleEmailOtp() && this._googleFirebaseAuth.hasPendingRedirectParams()
    );
  }

  /**
   * Firebase signInWithRedirect return — bootstrap user, oauth flag, or URL params.
   * Unlike Entra, Firebase often lands back on /login?provider=google without ?code=.
   */
  isReturningFromFirebaseSocialRedirect(): boolean {
    if (!this.useGoogleEmailOtp()) {
      return false;
    }
    if (this.peekSocialOAuthReturnFlag()) {
      return true;
    }
    const boot = getFirebaseRedirectBootstrapState();
    if (boot.user) {
      return true;
    }
    return this._googleFirebaseAuth.hasPendingRedirectParams();
  }

  /** Persist Lovable return URL across Entra loginRedirect navigation. */
  persistSocialDeepLink(
    returnUrl: string | null,
    provider: SocialProvider,
    app?: string | null
  ) {
    logGoogleFlow('auth.service:persistSocialDeepLink', {
      provider,
      returnUrl,
      app: app ?? this.resolvePostAuthApp(),
    });
    try {
      this._entraAuthService.clearSocialOAuthAbortedFlag();
      if (returnUrl) {
        sessionStorage.setItem('he_auth_return_url', returnUrl);
      }
      const signupSource = this.resolveRegisterSignupSource();
      if (signupSource) {
        sessionStorage.setItem(AuthService.signupSourceSessionKey, signupSource);
      }
      this.capturePostAuthApp(app ?? this.resolvePostAuthApp());
      sessionStorage.setItem('he_social_redirect_pending', provider);
      if (provider === 'google' || provider === 'apple') {
        this.registerSignUpAuthMethod = provider;
        this.markFirebaseSocialSignIn(provider);
      }
      if (provider === 'google' && POST_LOGIN_GOOGLE_LINK_ENABLED) {
        sessionStorage.setItem('he_pending_google_link', '1');
      }
    } catch {
      // ignore
    }
  }

  /** Read pending Lovable social redirect without clearing session keys. */
  peekSocialDeepLink(): {
    provider: SocialProvider | null;
    returnUrl: string | null;
  } {
    try {
      const provider = sessionStorage.getItem(
        'he_social_redirect_pending'
      ) as SocialProvider | null;
      const returnUrl = sessionStorage.getItem('he_auth_return_url');
      return {
        provider:
          provider === 'google' || provider === 'apple' ? provider : null,
        returnUrl,
      };
    } catch {
      return { provider: null, returnUrl: null };
    }
  }

  /** User started Lovable ?provider=google; link google.com after email sign-in. */
  hasPendingGoogleLink(): boolean {
    if (!POST_LOGIN_GOOGLE_LINK_ENABLED) {
      this.clearPendingGoogleLink();
      return false;
    }
    try {
      return sessionStorage.getItem('he_pending_google_link') === '1';
    } catch {
      return false;
    }
  }

  clearPendingGoogleLink(): void {
    try {
      sessionStorage.removeItem('he_pending_google_link');
    } catch {
      // ignore
    }
  }

  getGoogleLinkStatus(): Observable<{ googleLinked: boolean }> {
    return from(this.resolveAccountsApiBearerToken()).pipe(
      switchMap((token) =>
        this._dataService.getWithAuthToken(
          'api/v1/auth/identity/link/google/status',
          token
        )
      )
    );
  }

  /**
   * Link Google federated identity to signed-in Entra user (Graph).
   * Requires email OTP session + GOOGLE_OAUTH_* on API + googleOAuthClientId in Angular env.
   */
  async linkGoogleToEntraAccount(): Promise<{
    linked: boolean;
    alreadyLinked: boolean;
  }> {
    if (!this._googleLinkService.isConfigured) {
      throw new Error(
        'Google linking is not configured (googleOAuthClientId).'
      );
    }
    const code = await this._googleLinkService.requestAuthCode();
    const token = await this.resolveAccountsApiBearerToken();
    return firstValueFrom(
      this._dataService.post(
        'api/v1/auth/identity/link/google',
        { code, redirectUri: 'postmessage' },
        token
      )
    );
  }

  /**
   * After email login, complete one-time Google link when user came from ?provider=google.
   */
  async tryCompletePendingGoogleLink(): Promise<boolean> {
    if (!POST_LOGIN_GOOGLE_LINK_ENABLED) {
      this.clearPendingGoogleLink();
      return false;
    }
    if (!this.hasPendingGoogleLink()) {
      return false;
    }
    if (!this._googleLinkService.isConfigured) {
      return false;
    }
    try {
      const status = await firstValueFrom(this.getGoogleLinkStatus());
      if (status.googleLinked) {
        this.clearPendingGoogleLink();
        return true;
      }
      await this.linkGoogleToEntraAccount();
      this.clearPendingGoogleLink();
      return true;
    } catch (err) {
      console.error('Google account link failed', err);
      return false;
    }
  }

  /** Restore returnUrl after Entra redirect and clear pending flag. */
  consumeSocialDeepLink(): {
    provider: SocialProvider | null;
    returnUrl: string | null;
  } {
    try {
      const provider = sessionStorage.getItem(
        'he_social_redirect_pending'
      ) as SocialProvider | null;
      const returnUrl = sessionStorage.getItem('he_auth_return_url');
      sessionStorage.removeItem('he_social_redirect_pending');
      sessionStorage.removeItem('he_auth_return_url');
      if (returnUrl) {
        this.captureDeepLinkReturnUrl(returnUrl);
      }
      return {
        provider:
          provider === 'google' || provider === 'apple' ? provider : null,
        returnUrl,
      };
    } catch {
      return { provider: null, returnUrl: null };
    }
  }

  /** True when we still need to finish Accounts provisioning after Entra redirect. */
  hasPendingSocialRedirect(): boolean {
    try {
      return !!sessionStorage.getItem('he_social_redirect_pending');
    } catch {
      return false;
    }
  }

  /** Drop stale Google/Apple redirect flag; keep Lovable returnUrl in sessionStorage. */
  clearStaleSocialRedirectPending(): void {
    try {
      sessionStorage.removeItem('he_social_redirect_pending');
      sessionStorage.removeItem('he_pending_google_link');
      sessionStorage.removeItem('he_social_oauth_return');
    } catch {
      // ignore
    }
  }

  private static readonly SOCIAL_REDIRECT_ATTEMPT_COUNT =
    'he_social_redirect_attempt_count';
  private static readonly SOCIAL_REDIRECT_ATTEMPT_TS =
    'he_social_redirect_attempt_ts';
  private static readonly SOCIAL_REDIRECT_ATTEMPT_WINDOW_MS = 120_000;
  private static readonly SOCIAL_REDIRECT_ATTEMPT_MAX = 2;

  /** Prevent /redirect ↔ Google infinite loops when MSAL never attaches an account. */
  canAutoStartSocialRedirect(): boolean {
    try {
      const now = Date.now();
      const ts = Number(
        sessionStorage.getItem(AuthService.SOCIAL_REDIRECT_ATTEMPT_TS) || '0'
      );
      if (
        !ts ||
        now - ts > AuthService.SOCIAL_REDIRECT_ATTEMPT_WINDOW_MS
      ) {
        return true;
      }
      const count = Number(
        sessionStorage.getItem(AuthService.SOCIAL_REDIRECT_ATTEMPT_COUNT) || '0'
      );
      return count < AuthService.SOCIAL_REDIRECT_ATTEMPT_MAX;
    } catch {
      return true;
    }
  }

  recordSocialRedirectAttempt(): void {
    try {
      const now = Date.now();
      const ts = Number(
        sessionStorage.getItem(AuthService.SOCIAL_REDIRECT_ATTEMPT_TS) || '0'
      );
      let count = Number(
        sessionStorage.getItem(AuthService.SOCIAL_REDIRECT_ATTEMPT_COUNT) || '0'
      );
      if (
        !ts ||
        now - ts > AuthService.SOCIAL_REDIRECT_ATTEMPT_WINDOW_MS
      ) {
        count = 0;
      }
      sessionStorage.setItem(AuthService.SOCIAL_REDIRECT_ATTEMPT_TS, String(now));
      sessionStorage.setItem(
        AuthService.SOCIAL_REDIRECT_ATTEMPT_COUNT,
        String(count + 1)
      );
      logGoogleFlow('auth.service:recordSocialRedirectAttempt', {
        count: count + 1,
      });
    } catch {
      // ignore
    }
  }

  clearSocialRedirectAttemptWindow(): void {
    try {
      sessionStorage.removeItem(AuthService.SOCIAL_REDIRECT_ATTEMPT_COUNT);
      sessionStorage.removeItem(AuthService.SOCIAL_REDIRECT_ATTEMPT_TS);
    } catch {
      // ignore
    }
  }

  /** Set on /redirect after successful loginRedirect; consumed on /login to finish OAuth. */
  markSocialOAuthReturn(): void {
    logGoogleFlow('auth.service:markSocialOAuthReturn');
    try {
      sessionStorage.setItem('he_social_oauth_return', '1');
    } catch {
      // ignore
    }
  }

  /**
   * Set on /redirect after loginRedirect; consumed once on /login so we finish OAuth
   * even when the URL still has ?provider=google (from redirect-bridge).
   */
  consumeSocialOAuthReturnFlag(): boolean {
    try {
      const v = sessionStorage.getItem('he_social_oauth_return') === '1';
      if (v) {
        sessionStorage.removeItem('he_social_oauth_return');
      }
      return v;
    } catch {
      return false;
    }
  }

  /** True when /redirect sent us back to finish OAuth (do not wipe session on ?provider=). */
  peekSocialOAuthReturnFlag(): boolean {
    try {
      return sessionStorage.getItem('he_social_oauth_return') === '1';
    } catch {
      return false;
    }
  }

  /**
   * Fresh ?provider=google|apple from 8081/8080 — drop stale OAuth/pending flags
   * before MSAL sign-out and loginRedirect.
   */
  clearSessionStorageForNewSocialLogin(): void {
    this.clearAllSessionStorage('new-social-login');
  }

  /**
   * Wipe all localStorage + sessionStorage when presenting the guest login modal.
   * IdP sessions (Firebase / MSAL) are cleared best-effort in the background.
   */
  clearAllBrowserStorageForGuestLogin(): void {
    void this.clearAllBrowserStorageAsync('guest-login-modal');
  }

  /**
   * Wipe all browser storage immediately before Google / Apple redirect.
   * OAuth pending flags are written afterward via {@link persistSocialDeepLink}.
   */
  async clearAllBrowserStorageBeforeSocialRedirect(
    provider: SocialProvider,
  ): Promise<void> {
    await this.clearAllBrowserStorageAsync(`social-redirect-${provider}`);
  }

  private async clearAllBrowserStorageAsync(reason: string): Promise<void> {
    this.stopPeriodicSessionRefresh();
    this.refreshSessionPromise = null;
    this.clearEntraBffFlow();
    this.lastAccountsIdentity = null;

    await this.signOutFirebaseSession().catch(() => undefined);
    try {
      await this._entraAuthService.signOut({ localOnly: true });
    } catch {
      // ignore
    }
    this._entraAuthService.clearMsalBrowserCache();

    this.clearLocalStorage(reason);
    this.clearSessionStorage(reason);
  }

  /** Finish social login after loginRedirect return (MSAL cache already has account). */
  completeSocialAfterRedirect(): Observable<ValidOTPResponse | LoginResponse> {
    return from(this.finishSocialAfterRedirect());
  }

  private async finishSocialAfterRedirect(): Promise<ValidOTPResponse | LoginResponse> {
    if (this.useGoogleEmailOtp()) {
      return this.finishFirebaseSocialAfterRedirect();
    }
    logGoogleFlow('auth.service:finishSocialAfterRedirect:start', {
      pending: this.peekSocialDeepLink(),
    });
    const account = await this._entraAuthService.getCurrentAccount();
    console.log('account', account, '_______________________________');
    if (!account) {
      logGoogleFlow('auth.service:finishSocialAfterRedirect:noAccount');
      throw {
        error: {
          message:
            'Sign-in completed, but no account was found. Please try again.',
        },
      };
    }
    const email =
      account.getAccount?.()?.username || account.getUsername?.() || '';
    const pending = this.peekSocialDeepLink();
    if (pending.provider === 'google' || pending.provider === 'apple') {
      this.registerSignUpAuthMethod = pending.provider;
    }
    logGoogleFlow('auth.service:finishSocialAfterRedirect:provisioning', {
      email,
      provider: pending.provider,
    });
    return this.toValidOtpFromAccount(account, email);
  }

  private async finishFirebaseSocialAfterRedirect(): Promise<ValidOTPResponse> {
    logGoogleFlow('auth.service:finishFirebaseSocialAfterRedirect:start', {
      pending: this.peekSocialDeepLink(),
    });
    const pending = this.peekSocialDeepLink();
    const { user, error } =
      await this._googleFirebaseAuth.completeRedirectSignIn();
    if (error) {
      throw {
        error: {
          message: error.message || 'Sign-in did not finish. Please try again.',
        },
      };
    }
    if (!user) {
      throw {
        error: {
          message:
            'Sign-in completed, but no account was found. Please try again.',
        },
      };
    }
    if (pending.provider === 'google' || pending.provider === 'apple') {
      this.registerSignUpAuthMethod = pending.provider;
      this.markFirebaseSocialSignIn(pending.provider);
    }
    const idToken = await this._googleFirebaseAuth.getIdToken(user);
    const refreshToken = user.refreshToken?.trim() || '';
    const email = user.email?.trim().toLowerCase() || '';
    if (!email) {
      throw {
        error: {
          message:
            'Sign-in succeeded but no email was returned. Use email sign-in or try another account.',
        },
      };
    }
    await this.provisionAccountsUser(email, idToken);
    this.clearEntraBffFlow();
    this.clearSignedOutLocally();
    this.persistAuthData(email, {
      accessToken: idToken,
      refreshToken,
    });
    this.upsertUserCache(email);
    const registeredUser = (
      this.lastAccountsIdentity as Record<string, unknown> | null
    )?.['user'] as { id?: string } | undefined;
    return {
      status: 'Token',
      response: {
        accessToken: idToken,
        refreshToken,
      },
      userId: registeredUser?.id ? String(registeredUser.id) : '',
    };
  }

  resendLoginCode(): Observable<LoginResponse | ValidOTPResponse> {
    return from(this.resendEntraCode());
  }

  getSmsMfaStatus(): Observable<SmsMfaStatusResponse> {
    return from(this.resolveAccountsApiBearerToken()).pipe(
      switchMap((token) =>
        this._dataService.getWithAuthToken(
          'api/v1/auth/identity/sms-mfa/status',
          token
        )
      )
    );
  }

  enableSmsMfa(): Observable<SmsMfaStatusResponse> {
    return from(this.resolveAccountsApiBearerToken()).pipe(
      switchMap((token) =>
        this._dataService.post(
          'api/v1/auth/identity/sms-mfa/enable',
          {},
          token
        )
      )
    );
  }

  disableSmsMfa(): Observable<SmsMfaStatusResponse> {
    return from(this.resolveAccountsApiBearerToken()).pipe(
      switchMap((token) =>
        this._dataService.post(
          'api/v1/auth/identity/sms-mfa/disable',
          {},
          token
        )
      )
    );
  }

  /**
   * Opt in to the SMS MFA group, then start Entra sign-in so the user can
   * register their phone and verify the SMS code.
   */
  startSmsMfaEnableFlow(
    email: string,
    phone: string
  ): Observable<LoginResponse | ValidOTPResponse> {
    const normalizedPhone = Utils.normalizePhoneForEntra(phone);
    this.smsMfaEnableInProgress = true;
    this.pendingSmsMfaEnablePhone = normalizedPhone;
    return from(this.resolveAccountsApiBearerToken()).pipe(
      switchMap(() => this.enableSmsMfa()),
      switchMap(() => from(this.startEntraSignIn(email, undefined, true, false)))
    );
  }

  /**
   * Use persisted Accounts auth when available; otherwise hydrate or refresh
   * from the active Entra Custom Auth session (MSAL account ID token).
   */
  async ensureAccountsSessionFromEntra(): Promise<string | null> {
    if (this.wasSignedOutLocally()) {
      return null;
    }

    const authData = this._localStorageService.authorizationData;

    if (this.useGoogleEmailOtp()) {
      const firebaseUser = await this._googleFirebaseAuth.getCurrentUser();
      if (firebaseUser) {
        if (authData?.token && !this.isTokenExpiringSoon) {
          return authData.token;
        }
        try {
          const { idToken, refreshToken } =
            await this._googleFirebaseAuth.resolveSessionTokens(
              firebaseUser,
              true
            );
          const email =
            firebaseUser.email?.trim().toLowerCase() ||
            authData?.userName?.trim().toLowerCase() ||
            '';
          if (email) {
            this.persistAuthData(email, {
              accessToken: idToken,
              refreshToken:
                refreshToken || authData?.refreshToken?.trim() || '',
            });
          }
          return idToken;
        } catch {
          return authData?.token && !this.isTokenExpired ? authData.token : null;
        }
      }
      if (authData?.token && !this.isTokenExpiringSoon) {
        return authData.token;
      }
      return authData?.token && !this.isTokenExpired ? authData.token : null;
    }

    if (authData?.token && !this.isTokenExpiringSoon) {
      return authData.token;
    }

    if (authData?.token || authData?.refreshToken) {
      const refreshed = await this.refreshSession();
      if (refreshed) {
        return refreshed;
      }
    }

    const account = await this._entraAuthService.getCurrentAccount();
    if (!account) {
      return authData?.token && !this.isTokenExpired ? authData.token : null;
    }

    try {
      const refreshed = await this._entraAuthService.refreshAccountTokens(false);
      const idToken = refreshed?.idToken || account.getIdToken?.() || '';
      if (!idToken) {
        return null;
      }
      const email = this.resolveSessionEmail(account, authData?.userName);
      const refresh =
        (await this._entraAuthService.resolveEntraRefreshToken(account)) ||
        authData?.refreshToken?.trim() ||
        '';
      if (email) {
        this.persistAuthData(email, {
          accessToken: idToken,
          refreshToken: refresh,
        });
      }
      return idToken;
    } catch {
      const idToken = account.getIdToken?.() || '';
      if (!idToken) {
        return null;
      }
      const email = this.resolveSessionEmail(account, authData?.userName);
      const refresh =
        (await this._entraAuthService.resolveEntraRefreshToken(account)) ||
        authData?.refreshToken?.trim() ||
        '';
      if (email) {
        this.persistAuthData(email, {
          accessToken: idToken,
          refreshToken: refresh,
        });
      }
      return idToken;
    }
  }

  private async performSessionRefresh(): Promise<string | null> {
    const authData = this._localStorageService.authorizationData;

    if (this.useGoogleEmailOtp()) {
      const firebaseUser = await this._googleFirebaseAuth.getCurrentUser();
      if (firebaseUser) {
        try {
          const { idToken, refreshToken, email } =
            await this._googleFirebaseAuth.resolveSessionTokens(
              firebaseUser,
              true
            );
          const resolvedEmail =
            email || authData?.userName?.trim().toLowerCase() || '';
          if (resolvedEmail) {
            this.persistAuthData(resolvedEmail, {
              accessToken: idToken,
              refreshToken:
                refreshToken || authData?.refreshToken?.trim() || '',
            });
          }
          return idToken;
        } catch (err) {
          console.warn('Firebase token refresh failed', err);
        }
      }
    }

    const account = await this._entraAuthService.getCurrentAccount();
    const email = this.resolveSessionEmail(account, authData?.userName);

    try {
      const refreshed = await this._entraAuthService.refreshAccountTokens(true);
      if (refreshed?.idToken) {
        const rotated =
          (await this._entraAuthService.resolveEntraRefreshToken(account)) ||
          authData?.refreshToken?.trim() ||
          '';
        this.persistAuthData(email, {
          accessToken: refreshed.idToken,
          refreshToken: rotated,
        });
        return refreshed.idToken;
      }
    } catch (err) {
      console.warn('MSAL token refresh failed', err);
    }

    const storedRefresh = authData?.refreshToken?.trim();
    if (storedRefresh) {
      try {
        const response = await firstValueFrom(
          this.refreshSessionViaIdentityApi(storedRefresh)
        );
        const idToken = response.token?.trim() || '';
        if (idToken) {
          this.persistAuthData(response.email || email, {
            accessToken: idToken,
            refreshToken: response.refreshToken?.trim() || storedRefresh,
          });
          return idToken;
        }
      } catch (err) {
        console.warn('Identity session refresh failed', err);
      }

    }

    if (account) {
      const idToken = account.getIdToken?.() || '';
      if (idToken && !this.isTokenExpired) {
        return idToken;
      }
    }

    return authData?.token && !this.isTokenExpired ? authData.token : null;
  }

  private pickAccountsBearerFromEntraRefresh(
    response: EntraRefreshResponse
  ): string {
    const tokens = response?.tokens;
    const idToken = tokens?.idToken?.trim() || '';
    if (idToken && this.isAccountsApiCompatibleToken(idToken)) {
      return idToken;
    }
    const accessToken = tokens?.accessToken?.trim() || '';
    if (accessToken && this.isAccountsApiCompatibleToken(accessToken)) {
      return accessToken;
    }
    return idToken || accessToken;
  }

  private resolveSessionEmail(
    account: CustomAuthAccountData | null,
    fallback = ''
  ): string {
    return (
      account?.getAccount?.()?.username ||
      account?.getUsername?.() ||
      fallback ||
      ''
    );
  }

  private async resolveAccountsApiBearerToken(): Promise<string> {
    const token = await this.ensureAccountsSessionFromEntra();
    if (token) {
      return token;
    }
    throw {
      error: {
        message: 'Sign in to manage SMS two-factor authentication.',
      },
    };
  }

  clearSmsMfaEnableFlow() {
    this.smsMfaEnableInProgress = false;
    this.pendingSmsMfaEnablePhone = '';
  }

  /** Scope the identity session to a workplace without minting a legacy HS256 JWT. */
  getAuthTokenForCompany(
    userId: string,
    companyId: string
  ): Observable<AuthToken> {
    storeHandoffOrganization(userId, companyId);
    const authData = this._localStorageService.authorizationData;
    const accessToken = (authData?.token || '').trim();
    const refreshToken = (authData?.refreshToken || '').trim();
    if (!accessToken || this.isLegacyAccessToken(accessToken)) {
      return throwError(
        () => new Error('Identity session required to select a company.')
      );
    }
    return of({ accessToken, refreshToken });
  }

  setHandoffOrganization(userId: string, companyId: string): void {
    storeHandoffOrganization(userId, companyId);
  }

  getHandoffOrganization(): { userId: string; companyId: string } | null {
    return readHandoffOrganization();
  }

  clearHandoffOrganization(): void {
    clearStoredHandoffOrganization();
  }

  resolveActiveOrganizationId(): string | null {
    return readActiveOrganizationId();
  }

  isLegacyAccessToken(token: string | null | undefined): boolean {
    const payload = this.parseJwtPayload(String(token || '').trim());
    if (!payload) {
      return false;
    }
    const iss = String(payload['iss'] ?? '');
    if (iss.includes('securetoken.google.com')) {
      return false;
    }
    if (payload['oid']) {
      return false;
    }
    return !!payload['companyId'] && payload['type'] !== 'refresh_token';
  }

  isIdentityAccessToken(token?: string | null): boolean {
    const raw = String(token ?? this._localStorageService.authorizationData?.token ?? '').trim();
    if (!raw || this.isLegacyAccessToken(raw)) {
      return false;
    }
    const payload = this.parseJwtPayload(raw);
    if (!payload) {
      return false;
    }
    const iss = String(payload['iss'] ?? '');
    if (iss.includes('securetoken.google.com')) {
      return true;
    }
    if (payload['oid']) {
      return true;
    }
    return !payload['companyId'];
  }

  private parseJwtPayload(token: string): Record<string, unknown> | null {
    try {
      return Utils.parseJwt(token) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  /** Refresh Entra tokens via unified Accounts identity session API. */
  refreshSessionViaIdentityApi(
    refreshToken: string
  ): Observable<SessionRefreshResponse> {
    return this._dataService.post('api/v1/auth/identity/session/refresh', {
      refreshToken,
    });
  }

  /** @deprecated Prefer refreshSessionViaIdentityApi */
  refreshEntraTokens(refreshToken: string): Observable<EntraRefreshResponse> {
    return this.refreshSessionViaIdentityApi(refreshToken).pipe(
      map((response) => ({
        status: response.status,
        tokens: {
          accessToken: response.token,
          refreshToken: response.refreshToken,
          idToken: response.token,
          expiresIn: response.expiresIn,
          tokenType: response.tokenType,
        },
      }))
    );
  }

  /** Returns a valid Accounts API bearer token, refreshing when needed. */
  async ensureValidAccessToken(): Promise<string | null> {
    return this.ensureAccountsSessionFromEntra();
  }

  /** Load signed-in user profile from Accounts API. */
  getCurrentUser(): Observable<CurrentUserResponse> {
    return from(this.ensureValidAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('Not authenticated'));
        }
        return this._dataService.getWithAuthToken(
          'api/v1/auth/currentUser',
          token
        );
      })
    );
  }

  /**
   * Display name from Accounts user record (firstName / lastName).
   * Falls back to email-based name when the API is unavailable.
   */
  async resolveUserDisplayName(fallbackEmail = ''): Promise<string> {
    try {
      const user = await firstValueFrom(this.getCurrentUser());
      const fromProfile = this.formatNameFromCurrentUser(user);
      if (fromProfile) {
        return fromProfile;
      }
    } catch {
      // Fall back when user is not provisioned yet or API rejects the token.
    }
    return this.formatDisplayNameFromEmail(fallbackEmail);
  }

  formatNameFromCurrentUser(user: CurrentUserResponse | null | undefined): string {
    if (!user) {
      return '';
    }
    const firstName = this.sanitizeUserName(user.firstName || '');
    const lastName = this.sanitizeUserName(user.lastName || '');
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) {
      return fullName;
    }
    return firstName || lastName || '';
  }

  private formatDisplayNameFromEmail(email: string): string {
    const authData = this._localStorageService.authorizationData;
    if (authData?.token) {
      try {
        const claims = Utils.parseJwt(authData.token);
        const fromToken = (
          claims?.given_name ||
          claims?.name ||
          claims?.preferred_username ||
          ''
        )
          .toString()
          .trim();
        if (fromToken && !fromToken.includes('@')) {
          return fromToken.charAt(0).toUpperCase() + fromToken.slice(1);
        }
      } catch {
        // Fall through to email-based display name.
      }
    }

    const local = (email.split('@')[0] || email).trim();
    const firstPart = local.split(/[._+\-]/)[0] || local;
    if (!firstPart) {
      return 'there';
    }
    return firstPart.charAt(0).toUpperCase() + firstPart.slice(1);
  }

  /** Refresh stored session every 5 minutes while the app is open. */
  startPeriodicSessionRefresh(): void {
    if (this.sessionRefreshTimer) {
      console.log('[he-auth][periodic-refresh] wa-account-v2: timer already running');
      return;
    }
    console.log('[he-auth][periodic-refresh] wa-account-v2: starting interval', {
      intervalMs: this.sessionRefreshIntervalMs,
      at: new Date().toISOString(),
    });
    this.sessionRefreshTimer = setInterval(() => {
      console.log('[he-auth][periodic-refresh] wa-account-v2: tick', {
        at: new Date().toISOString(),
      });
      void this.refreshSessionIfNeeded();
    }, this.sessionRefreshIntervalMs);
  }

  stopPeriodicSessionRefresh(): void {
    if (this.sessionRefreshTimer) {
      clearInterval(this.sessionRefreshTimer);
      this.sessionRefreshTimer = null;
    }
  }

  private async refreshSessionIfNeeded(): Promise<void> {
    console.log('[he-auth][periodic-refresh] wa-account-v2: refreshSessionIfNeeded');
    if (this.wasSignedOutLocally()) {
      console.log('[he-auth][periodic-refresh] wa-account-v2: skipped (signed out locally)');
      return;
    }
    const authData = this._localStorageService.authorizationData;
    const hasLocalSession = !!(authData?.refreshToken || authData?.token);
    if (!hasLocalSession) {
      if (this.useGoogleEmailOtp()) {
        const firebaseUser = await this._googleFirebaseAuth.getCurrentUser();
        if (firebaseUser) {
          console.log(
            '[he-auth][periodic-refresh] wa-account-v2: no local token but Firebase user present — refreshing'
          );
          await this.refreshSession('periodic');
          return;
        }
      }
      const account = await this._entraAuthService.getCurrentAccount();
      if (!account) {
        console.log('[he-auth][periodic-refresh] wa-account-v2: skipped (no token/refresh, no MSAL account)');
        return;
      }
      console.log('[he-auth][periodic-refresh] wa-account-v2: no local token but MSAL account present — refreshing');
    } else {
      console.log('[he-auth][periodic-refresh] wa-account-v2: local session present — refreshing', {
        hasRefreshToken: !!authData?.refreshToken?.trim(),
        hasAccessToken: !!authData?.token,
      });
    }
    await this.refreshSession('periodic');
  }

  /**
   * Renew the stored session token using MSAL, Entra BFF, or legacy refresh.
   * Concurrent callers share one in-flight refresh.
   */
  async refreshSession(trigger = 'on-demand'): Promise<string | null> {
    console.log('[he-auth][session-refresh] wa-account-v2: refreshSession', {
      trigger,
      at: new Date().toISOString(),
    });
    if (this.refreshSessionPromise) {
      return this.refreshSessionPromise;
    }

    this.refreshSessionPromise = this.performSessionRefresh()
      .then((token) => {
        console.log('[he-auth][session-refresh] wa-account-v2: finished', {
          trigger,
          ok: !!token,
          at: new Date().toISOString(),
        });
        return token;
      })
      .finally(() => {
        this.refreshSessionPromise = null;
      });
    return this.refreshSessionPromise;
  }

  hideOrPinCompany(request: any): Observable<void> {
    return this._dataService.post('api/v1/users/pin-hide-company', request);
  }

  activateAccount(token: string): Observable<any> {
    return this._dataService.post('api/v1/auth/activate', { token });
  }

  getUserLicenses(): Observable<CompanyProductLicense[]> {
    return this._dataService.get('api/v1/companies/company_licenses');
  }

  setUserIpAddress() {
    return this._dataService
      .get(`https://ipinfo.io/json?token=b67e18a15eba1c`, false)
      .subscribe((res) => {
        this._localStorageService.ipAddress = res?.ip || null;
        this._localStorageService.geoLocation = this.getFormattedAddress(res);
      });
  }

  logout(): void {
    void this.logoutAndRedirect({ everywhere: true });
  }

  /**
   * Lovable → Accounts logout deep-link:
   *   /logout?token=...&refreshToken=...&email=...&returnUrl=...&everywhere=1
   */
  hydrateAuthFromLogoutDeepLink(params: {
    token?: string | null;
    refreshToken?: string | null;
    email?: string | null;
  }): boolean {
    const token = String(params.token || '').trim();
    if (!token) {
      return false;
    }
    const email = String(params.email || '').trim();
    const refreshToken = String(params.refreshToken || '').trim();
    this.persistAuthData(email, {
      accessToken: token,
      refreshToken,
    });
    return true;
  }

  /**
   * Sign out of Entra + clear local auth.
   * When `everywhere` is true and a token exists, POST logout-everywhere must succeed
   * before local session data is cleared.
   * Optional deep-link from Lovable/Aya:
   *   /logout?returnUrl=<encoded>&everywhere=1
   * Defaults to environment.ayaHealthChatAppUrl (dev-www) after logout.
   */
  async logoutAndRedirect(options?: {
    returnUrl?: string | null;
    everywhere?: boolean;
    /** Reload accounts login instead of sending the user to dev-www. */
    stayOnAccountsApp?: boolean;
  }): Promise<void> {
    const everywhere = options?.everywhere === true;
    const stayOnAccountsApp = options?.stayOnAccountsApp === true;
    const explicitReturnUrl = options?.returnUrl
      ? Utils.sanitizeReturnUrl(String(options.returnUrl).trim())
      : null;

    const viaGoogleBrowserLogout =
      await this.shouldRedirectThroughGoogleBrowserLogout();

    this.stopPeriodicSessionRefresh();
    this.refreshSessionPromise = null;
    this.markSignedOutLocally();

    const token =
      this._localStorageService.authorizationData?.token || '';

    // Firebase must be cleared before server revoke / redirects — otherwise
    // dev-app can silently call getIdToken() and hand a new token to Clinic.
    await this.signOutFirebaseSession();

    if (everywhere && token) {
      try {
        await firstValueFrom(
          this._dataService.post(
            'api/v1/auth/identity/logout-everywhere',
            {},
            token
          )
        );
      } catch (err) {
        console.warn('[logout] logout-everywhere failed; continuing to redirect', err);
      }
    }

    // Entra / MSAL local clear (Firebase already signed out above).
    await this.clearSessionUntilSignedOut({ localOnly: true });
    this.clearEmailDeepLinkIntent();
    this.deepLinkReturnUrl = null;
    this.lastAccountsIdentity = null;

    if (viaGoogleBrowserLogout) {
      if (
        explicitReturnUrl &&
        this.isCrossAppLogoutChain(explicitReturnUrl)
      ) {
        this.redirectToLogoutReturnUrl(explicitReturnUrl);
        return;
      }
      // Firebase/local session is already cleared — finish on www (with relay),
      // not on accounts.google.com/Logout (no return path back to Aya apps).
      const returnUrl = this.resolveLogoutReturnUrl(
        explicitReturnUrl,
        everywhere
      );
      this.redirectToLogoutReturnUrl(returnUrl);
      return;
    }

    if (stayOnAccountsApp) {
      window.location.replace('/login?loggedOut=1');
      return;
    }

    const returnUrl = this.resolveLogoutReturnUrl(
      explicitReturnUrl || this.deepLinkReturnUrl,
      everywhere
    );

    if (returnUrl) {
      this.redirectToLogoutReturnUrl(returnUrl);
      return;
    }

    this.redirectToLogoutReturnUrl(
      this.defaultLogoutLandingUrl(explicitReturnUrl)
    );
  }

  /** Final landing page after sign-out (dev-www / qa-aya www entry). */
  private defaultLogoutLandingUrl(hint?: string | null): string {
    const tier = this.resolveLogoutTierHint(hint);
    const www = Utils.sanitizeReturnUrl(
      tier === 'qa' ? wwwAppUrlForTier('qa') : environment.ayaHealthChatAppUrl
    );
    if (www) {
      try {
        const url = new URL(www);
        url.searchParams.set('loggedOut', '1');
        return url.href;
      } catch {
        return www;
      }
    }
    const aya = Utils.sanitizeReturnUrl(
      tier === 'qa' ? ayaAppUrlForTier('qa') : environment.ayaAppUrl
    );
    if (aya) {
      try {
        const url = new URL(aya);
        url.searchParams.set('loggedOut', '1');
        return url.href;
      } catch {
        return aya;
      }
    }
    return '/login?loggedOut=1';
  }

  /** Origins to visit with ?loggedOut=1 before the final URL (clears other app sessions). */
  private logoutRelayOrigins(hint?: string | null): string[] {
    const origins: string[] = [];
    const tier = this.resolveLogoutTierHint(hint);
    const aya = Utils.sanitizeReturnUrl(
      tier === 'qa' ? ayaAppUrlForTier('qa') : environment.ayaAppUrl
    );
    const www = Utils.sanitizeReturnUrl(
      tier === 'qa' ? wwwAppUrlForTier('qa') : environment.ayaHealthChatAppUrl
    );
    if (!aya || !www) {
      return origins;
    }
    try {
      const ayaOrigin = new URL(aya).origin;
      const wwwOrigin = new URL(www).origin;
      if (ayaOrigin !== wwwOrigin) {
        origins.push(ayaOrigin);
      }
    } catch {
      // ignore
    }
    return origins;
  }

  private isAccountsAppReturnUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      if (typeof window !== 'undefined' && parsed.origin === window.location.origin) {
        return true;
      }
      const host = parsed.hostname.toLowerCase();
      return (
        host === 'dev-auth.healthexam.ai' ||
        host.startsWith('dev-auth.') ||
        host === 'qa-auth.healthexam.ai' ||
        host.startsWith('qa-auth.') ||
        host === 'auth.healthexam.ai'
      );
    } catch {
      return false;
    }
  }

  private isCrossAppLogoutChain(url: string): boolean {
    try {
      const parsed = new URL(url);
      return (
        parsed.searchParams.get('loggedOut') === '1' &&
        parsed.searchParams.has('returnUrl')
      );
    } catch {
      return false;
    }
  }

  /**
   * Always finish on dev-www; relay through dev-aya (and other origins) to clear their storage.
   */
  private resolveLogoutReturnUrl(
    explicit: string | null,
    everywhere: boolean,
    landing?: string
  ): string {
    const defaultLanding = landing ?? this.defaultLogoutLandingUrl(explicit);
    let sanitizedExplicit =
      explicit && !this.isAccountsAppReturnUrl(explicit) ? explicit : null;

    // Client apps may send a pre-built relay; unwrap to final www and rebuild relay here.
    if (sanitizedExplicit && this.isCrossAppLogoutChain(sanitizedExplicit)) {
      const finalLanding = resolveFinalLogoutLandingUrl(sanitizedExplicit);
      const sanitizedFinal = finalLanding
        ? Utils.sanitizeReturnUrl(finalLanding)
        : null;
      if (sanitizedFinal) {
        sanitizedExplicit = this.prepareLogoutReturnUrl(sanitizedFinal);
      }
    }

    if (
      sanitizedExplicit &&
      this.isCrossAppLogoutChain(sanitizedExplicit) &&
      !isGoogleBrowserLogoutUrl(defaultLanding)
    ) {
      return sanitizedExplicit;
    }

    if (!everywhere) {
      return defaultLanding;
    }

    const relayOrigins = this.logoutRelayOrigins(sanitizedExplicit ?? explicit);
    if (!relayOrigins.length) {
      return defaultLanding;
    }
    return buildCrossAppRelayReturnUrl(relayOrigins, defaultLanding);
  }

  /** Infer dev vs qa from logout returnUrl chain (shared dev-auth serves both tiers). */
  private resolveLogoutTierHint(
    ...urls: (string | null | undefined)[]
  ): AyaDeployTier {
    for (const url of urls) {
      if (resolveAyaDeployTierFromReturnUrl(url) === 'qa') {
        return 'qa';
      }
    }
    try {
      const stored = sessionStorage.getItem('he_auth_return_url');
      if (resolveAyaDeployTierFromReturnUrl(stored) === 'qa') {
        return 'qa';
      }
    } catch {
      // ignore
    }
    return 'dev';
  }

  /** True when Accounts storage, Firebase, or MSAL still holds an active session. */
  async hasPersistedSession(): Promise<boolean> {
    if (this._localStorageService.authorizationData?.token) {
      return true;
    }
    if (this.useGoogleEmailOtp()) {
      const firebaseUser = await this._googleFirebaseAuth.getCurrentUser();
      if (firebaseUser) {
        return true;
      }
    }
    return !!(await this._entraAuthService.getCurrentAccount());
  }

  /** Sign out Firebase Identity Platform session (Google email OTP / social). */
  private async signOutFirebaseSession(): Promise<void> {
    if (!this.useGoogleEmailOtp() || !this._googleFirebaseAuth.isConfigured()) {
      return;
    }
    try {
      await this._googleFirebaseAuth.signOut();
    } catch {
      // Firebase may already be signed out.
    }
    this.clearFirebaseSocialSignInMarker();
  }

  /**
   * Retry local + Entra sign-out until no session remains (or max attempts).
   * Firebase is signed out first via {@link signOutFirebaseSession}.
   */
  async clearSessionUntilSignedOut(options?: {
    localOnly?: boolean;
  }): Promise<void> {
    this.stopPeriodicSessionRefresh();
    this.refreshSessionPromise = null;
    this.markSignedOutLocally();

    await this.signOutFirebaseSession();

    try {
      for (let attempt = 1; attempt <= this.logoutClearMaxAttempts; attempt++) {
        await this._entraAuthService.signOut({
          localOnly: options?.localOnly === true,
        });
        this.clearAuthFromLocalStorage();
        this.markSignedOutLocally();

        if (!(await this.hasPersistedSession())) {
          return;
        }

        if (attempt < this.logoutClearMaxAttempts) {
          await this.delay(this.logoutClearDelayMs * attempt);
        }
      }

      this._entraAuthService.clearMsalBrowserCache();
      this.clearAuthFromLocalStorage();
      this.markSignedOutLocally();
    } finally {
      this.clearSessionStorageOnLogout();
      // sessionStorage.clear() removes the signed-out marker — persist again.
      this.markSignedOutLocally();
    }
  }

  private markFirebaseSocialSignIn(provider: 'google' | 'apple'): void {
    try {
      sessionStorage.setItem(
        AuthService.FIREBASE_SOCIAL_PROVIDER_KEY,
        provider
      );
      localStorage.setItem(AuthService.FIREBASE_SOCIAL_PROVIDER_KEY, provider);
    } catch {
      // ignore
    }
  }

  private clearFirebaseSocialSignInMarker(): void {
    try {
      sessionStorage.removeItem(AuthService.FIREBASE_SOCIAL_PROVIDER_KEY);
      localStorage.removeItem(AuthService.FIREBASE_SOCIAL_PROVIDER_KEY);
    } catch {
      // ignore
    }
  }

  /** Remove OAuth pending flags, returnUrl, signed-out marker, MSAL tab cache keys, etc. */
  private clearSessionStorageOnLogout(): void {
    this.clearAllSessionStorage('logout');
  }

  private clearLocalStorage(reason: string): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      console.log('[he-auth] localStorage.clear()', {
        reason,
        href: typeof window !== 'undefined' ? window.location.href : null,
      });
      localStorage.clear();
    } catch {
      // ignore
    }
  }

  private clearSessionStorage(reason: string): void {
    try {
      if (typeof sessionStorage === 'undefined') {
        return;
      }
      console.log('[he-auth] sessionStorage.clear()', {
        reason,
        href: typeof window !== 'undefined' ? window.location.href : null,
      });
      sessionStorage.clear();
    } catch {
      // ignore
    }
  }

  private clearAllSessionStorage(reason = 'unknown'): void {
    this.clearSessionStorage(reason);
  }

  /** Full local teardown after server-side logout (or on /login?loggedOut=1). */
  async finalizeGuestSession(): Promise<void> {
    await this.clearSessionUntilSignedOut();
    this.deepLinkReturnUrl = null;
    this.lastAccountsIdentity = null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** After logout, send client apps back without a token. */
  redirectToLogoutReturnUrl(returnUrl: string) {
    if (isGoogleBrowserLogoutUrl(returnUrl)) {
      window.location.href = returnUrl;
      return;
    }
    window.location.href = this.prepareLogoutReturnUrl(returnUrl);
  }

  private prepareLogoutReturnUrl(returnUrl: string): string {
    try {
      const url = new URL(returnUrl);
      url.searchParams.delete('token');
      url.searchParams.delete('email');
      url.searchParams.delete('q');
      url.searchParams.delete('d');
      url.searchParams.set('loggedOut', '1');
      return url.toString();
    } catch {
      return returnUrl;
    }
  }

  /** True when logout should pass through accounts.google.com/Logout. */
  private useGoogleBrowserLogoutOnSignOut(): boolean {
    if (environment.googleBrowserLogoutOnSignOut === false) {
      return false;
    }
    if (environment.googleBrowserLogoutOnSignOut === true) {
      return true;
    }
    return this.useGoogleEmailOtp();
  }

  private readPersistedFirebaseSocialProvider(): 'google' | 'apple' | null {
    try {
      const raw =
        localStorage.getItem(AuthService.FIREBASE_SOCIAL_PROVIDER_KEY) ||
        sessionStorage.getItem(AuthService.FIREBASE_SOCIAL_PROVIDER_KEY);
      return raw === 'google' || raw === 'apple' ? raw : null;
    } catch {
      return null;
    }
  }

  private async signedInViaGoogleInBrowser(): Promise<boolean> {
    if (this.readPersistedFirebaseSocialProvider() === 'google') {
      return true;
    }
    if (!this.useGoogleEmailOtp() || !this._googleFirebaseAuth.isConfigured()) {
      return false;
    }
    const user = await this._googleFirebaseAuth.getCurrentUser();
    return (
      user?.providerData?.some((provider) => provider.providerId === 'google.com') ??
      false
    );
  }

  private async shouldRedirectThroughGoogleBrowserLogout(): Promise<boolean> {
    if (!this.useGoogleBrowserLogoutOnSignOut()) {
      return false;
    }
    if (await this.signedInViaGoogleInBrowser()) {
      return true;
    }
    // Lovable logout deep-links often omit the Firebase social marker on dev-auth.
    return this.useGoogleEmailOtp();
  }

  redirectToUrl(url: string) {
    window.location.href = url;
  }

  persistAuthData = (email: string, authResponse: AuthToken): void => {
    if (this.isLegacyAccessToken(authResponse.accessToken)) {
      console.warn('[auth] Refusing to persist legacy company-scoped JWT.');
      return;
    }
    let expiresAt = new Date(Date.now() + 60 * 60 * 1000);
    if (authResponse.accessToken) {
      try {
        const jwtPayload = Utils.parseJwt(authResponse.accessToken);
        if (jwtPayload?.exp) {
          expiresAt = Utils.getTokenExpireDate(jwtPayload.exp);
        }
      } catch {
        // Non-JWT tokens: keep default expiry window.
      }
    }

    const authData = {
      token: authResponse.accessToken,
      refreshToken: (authResponse.refreshToken || '').trim(),
      userName: email,
      expiresAt,
    };

    this.clearSignedOutLocally();
    this._localStorageService.authorizationData = authData;
    this.upsertUserCache(authData.userName);
  };

  upsertUserCache(userName: string) {
    let cache = this._localStorageService.userCache;
    if (!cache?.length) {
      cache = [];
    }
    const matchingIndex = cache.findIndex((c) => c.userName === userName);
    if (matchingIndex === -1) {
      cache.push({ userName: userName });
    } else {
      cache[matchingIndex] = { userName: userName };
    }
    this._localStorageService.userCache = cache;
  }

  removeAccountFromUserCache(userName: string) {
    let cache = this._localStorageService.userCache;
    if (!cache?.length) {
      return;
    }
    this._localStorageService.userCache = cache.filter(
      (c) => c.userName !== userName
    );
  }

  get cachedUsers(): UserCache[] {
    return this._localStorageService.userCache;
  }

  /** Default post-login destination (Aya / Lovable) with Entra id token. */
  redirectToAyaApp(): void {
    const ayaUrl = Utils.sanitizeReturnUrl(environment.ayaAppUrl);
    if (ayaUrl) {
      void this.redirectToUrlWithTokenPrepared(ayaUrl).catch((err) => {
        console.error('[auth] redirectToAyaApp handoff failed', err);
      });
    }
  }

  getIdentityMe(): Observable<{
    user?: { hasUserAcceptedTerms?: boolean; id?: string };
  }> {
    return this._dataService.get('api/v1/auth/identity/me');
  }

  /** Dev only — Entra user id + app-only Graph token for /test/graph-user page. */
  getGraphUserTestSession(): Observable<{
    userId: string;
    accountsUserId?: string | null;
    email?: string | null;
    graphAccessToken: string;
    graphRequestUrl: string;
    select?: string;
    exchangeWebhookConfigured?: boolean;
  }> {
    return this._dataService.get('api/v1/auth/identity/test/graph-user');
  }

  /** Dev only — Azure Automation webhook for Exchange mailbox alias cleanup. */
  runGraphUserTestExchangeCleanup(body: {
    oldEmail: string;
    newEmail: string;
    user?: string;
    waitMs?: number;
  }): Observable<unknown> {
    return this._dataService.post(
      'api/v1/auth/identity/test/graph-user/exchange-cleanup',
      body
    );
  }

  /** Dev only — ACS Email test page (/test/acs-email). */
  sendAcsEmailTest(body: {
    to: string;
    subject?: string;
    plainText?: string;
    html?: string;
  }): Observable<{ to?: string; id?: string; status?: string; senderConfigured?: boolean }> {
    return this._dataService.post('api/v1/auth/identity/test/acs-email', body);
  }

  acceptTermsAndConditions(): Observable<unknown> {
    return this._dataService.post('api/v1/auth/identity/terms/accept', {});
  }

  isAyaAppHandoffUrl(returnUrl: string): boolean {
    return Utils.isAyaAppHandoffUrl(returnUrl);
  }

  storePendingAyaReturnUrl(returnUrl: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(this.pendingAyaReturnUrlKey, returnUrl);
  }

  consumePendingAyaReturnUrl(): string | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const raw = sessionStorage.getItem(this.pendingAyaReturnUrlKey);
    sessionStorage.removeItem(this.pendingAyaReturnUrlKey);
    return raw ? Utils.sanitizeReturnUrl(raw) : null;
  }

  /**
   * Before redirecting to Aya / www: require terms unless already accepted.
   * Returns false when navigation to /accept-terms was triggered.
   */
  async ensureTermsAcceptedBeforeAyaHandoff(returnUrl: string): Promise<boolean> {
    if (!this.isAyaAppHandoffUrl(returnUrl)) {
      return true;
    }

    await this.ensureAccountsUserProvisionedBeforeHandoff();

    let accepted = false;
    try {
      const me = await firstValueFrom(this.getIdentityMe());
      accepted = me?.user?.hasUserAcceptedTerms === true;
    } catch (err) {
      console.warn(
        '[auth] Could not verify terms acceptance; showing accept-terms.',
        err
      );
    }

    if (accepted) {
      return true;
    }

    this.storePendingAyaReturnUrl(returnUrl);
    await this._router.navigate(['/accept-terms']);
    return false;
  }

  /** After POST terms/accept — continue Aya token handoff. */
  async completePendingAyaHandoffAfterTerms(): Promise<void> {
    const returnUrl =
      this.consumePendingAyaReturnUrl() ||
      Utils.sanitizeReturnUrl(environment.ayaAppUrl);
    if (!returnUrl) {
      return;
    }
    await this.executeHandoffToReturnUrl(returnUrl);
  }

  /** Refresh session before Lovable / readable-token handoff (prod expects refreshToken). */
  async prepareHandoffToReturnUrl(returnUrl: string): Promise<void> {
    const needsHandoffPrep =
      Utils.usesReadableTokenReturnUrl(returnUrl) ||
      Utils.isAngularProductHandoffUrl(returnUrl);
    if (!needsHandoffPrep) {
      return;
    }
    await this.ensureAccountsSessionFromEntra();
    await this.ensureStoredRefreshTokenForHandoff();
    await this.ensureAccountsUserProvisionedBeforeHandoff();
    if (Utils.usesReadableTokenReturnUrl(returnUrl)) {
      const refresh =
        this._localStorageService.authorizationData?.refreshToken?.trim();
      if (!refresh) {
        console.warn(
          '[auth] Readable-token handoff is missing refreshToken; Aya apps cannot run session/refresh until the user signs in again with offline_access.'
        );
      }
    }
  }

  /** Persist Firebase or MSAL refresh token before redirecting to Aya / Lovable with ?refreshToken=. */
  private async ensureStoredRefreshTokenForHandoff(): Promise<void> {
    let authData = this._localStorageService.authorizationData;
    if (authData?.refreshToken?.trim()) {
      return;
    }

    if (this.useGoogleEmailOtp()) {
      const firebaseUser = await this._googleFirebaseAuth.getCurrentUser();
      const refresh = firebaseUser?.refreshToken?.trim();
      if (refresh) {
        const email =
          firebaseUser?.email?.trim().toLowerCase() ||
          authData?.userName?.trim().toLowerCase() ||
          '';
        const token =
          authData?.token ||
          (firebaseUser
            ? await this._googleFirebaseAuth.getIdToken(firebaseUser)
            : '');
        if (token && email) {
          this.persistAuthData(email, {
            accessToken: token,
            refreshToken: refresh,
          });
          console.log('[auth] Attached Firebase refresh token for returnUrl handoff');
        }
        return;
      }
    }

    const account = await this._entraAuthService.getCurrentAccount();
    let refresh = await this._entraAuthService.resolveEntraRefreshToken(account);
    if (!refresh) {
      for (let attempt = 0; attempt < 8 && !refresh; attempt++) {
        await this.delay(75);
        refresh = await this._entraAuthService.resolveEntraRefreshToken(account);
      }
    }
    if (!refresh) {
      try {
        await this._entraAuthService.refreshAccountTokens(true);
      } catch {
        // MSAL may still expose refresh token in cache after sign-in redirect.
      }
      refresh = await this._entraAuthService.resolveEntraRefreshToken(account);
    }
    if (!refresh) {
      return;
    }

    if (!authData?.token) {
      const idToken = account?.getIdToken?.() || '';
      const email =
        authData?.userName ||
        account?.getAccount?.()?.username ||
        account?.getUsername?.() ||
        '';
      if (idToken && email) {
        this.persistAuthData(email, {
          accessToken: idToken,
          refreshToken: refresh,
        });
        console.log('[auth] Attached Entra refresh token for returnUrl handoff');
      }
      return;
    }

    this.persistAuthData(authData.userName, {
      accessToken: authData.token,
      refreshToken: refresh,
    });
    console.log('[auth] Attached Entra refresh token for returnUrl handoff');
  }

  /**
   * Google/Apple can hand off to Aya after Entra tokens exist but before
   * identity/register ran (legacy path skipped provision when idToken was empty).
   */
  private async ensureAccountsUserProvisionedBeforeHandoff(): Promise<void> {
    if (
      (this.lastAccountsIdentity as { user?: { id?: string } } | null)?.user?.id
    ) {
      return;
    }
    const authData = this._localStorageService.authorizationData;
    const accessToken = authData?.token?.trim();
    if (!accessToken || !this.isAccountsApiCompatibleToken(accessToken)) {
      return;
    }
    const email = authData?.userName?.trim() || '';
    if (!email) {
      return;
    }
    try {
      await this.provisionAccountsUser(email, accessToken);
      console.log('[auth] Provisioned Accounts user before returnUrl handoff');
    } catch (err) {
      console.error('[auth] Accounts provision before handoff failed', err);
      throw err;
    }
  }

  /** Fill missing refreshToken on AuthToken from Firebase or MSAL before persist / handoff. */
  async hydrateAuthTokenRefreshToken(
    email: string,
    token: AuthToken
  ): Promise<AuthToken> {
    const existing = token.refreshToken?.trim();
    if (existing) {
      return token;
    }

    if (this.useGoogleEmailOtp()) {
      const firebaseUser = await this._googleFirebaseAuth.getCurrentUser();
      const refresh = firebaseUser?.refreshToken?.trim();
      if (refresh) {
        const hydrated: AuthToken = {
          accessToken: token.accessToken,
          refreshToken: refresh,
        };
        this.persistAuthData(email, hydrated);
        return hydrated;
      }
    }

    const refresh = await this._entraAuthService.resolveEntraRefreshToken();
    if (!refresh) {
      return token;
    }
    const hydrated: AuthToken = {
      accessToken: token.accessToken,
      refreshToken: refresh,
    };
    this.persistAuthData(email, hydrated);
    return hydrated;
  }

  async redirectToUrlWithTokenPrepared(returnUrl: string): Promise<void> {
    if (this.wasSignedOutLocally()) {
      return;
    }
    const sanitized = Utils.sanitizeReturnUrl(returnUrl);
    if (!sanitized) {
      throw {
        error: { message: 'Invalid return URL.' },
      };
    }
    if (!(await this.ensureTermsAcceptedBeforeAyaHandoff(sanitized))) {
      return;
    }
    await this.executeHandoffToReturnUrl(sanitized);
  }

  /** Token handoff after terms gate (or non-Aya destinations). */
  private async executeHandoffToReturnUrl(returnUrl: string): Promise<void> {
    await this.prepareHandoffToReturnUrl(returnUrl);
    if (Utils.usesReadableTokenReturnUrl(returnUrl)) {
      await this.ensureStoredRefreshTokenForHandoff();
      const refresh =
        this._localStorageService.authorizationData?.refreshToken?.trim();
      if (!refresh) {
        logGoogleFlow('auth.service:redirectToUrlWithTokenPrepared:missingRefresh', {
          returnUrl,
        });
        throw {
          error: {
            message:
              'Sign-in succeeded but a refresh token could not be obtained. Sign out everywhere, then sign in again.',
          },
        };
      }
    }
    this.redirectToUrlWithToken(returnUrl);
  }

  redirectToUrlWithToken(returnUrl: string) {
    if (this.wasSignedOutLocally()) {
      return;
    }
    const authData = this._localStorageService.authorizationData;
    const tokenParam = this._localStorageService.authorizationDataEncrypted;
    const machineId = this._localStorageService.machineId;
    const useReadableToken = Utils.usesReadableTokenReturnUrl(returnUrl);

    logGoogleFlow('auth.service:redirectToUrlWithToken', {
      returnUrl,
      useReadableToken,
      hasToken: !!authData?.token,
      hasRefresh: !!authData?.refreshToken,
    });

    try {
      const url = new URL(returnUrl);

      if (useReadableToken) {
        // Aya / Lovable apps expect a readable Entra Bearer token.
        if (authData?.token) {
          url.searchParams.set('token', authData.token);
        }
        if (authData?.refreshToken) {
          url.searchParams.set('refreshToken', authData.refreshToken);
        }
        if (authData?.userName) {
          url.searchParams.set('email', authData.userName);
        }
      } else {
        // HealthExam product apps use the encrypted auth blob.
        url.searchParams.set('q', tokenParam);
        if (machineId) {
          url.searchParams.set('d', machineId);
        }
        console.log('Redirecting to returnUrl with encrypted auth blob (q).', {
          returnUrl,
        });
      }

      const handoff = readHandoffOrganization();
      if (handoff?.companyId) {
        url.searchParams.set('companyId', handoff.companyId);
      }
      if (handoff?.userId) {
        url.searchParams.set('userId', handoff.userId);
      }

      logGoogleFlowRedirect(
        'auth.service:redirectToUrlWithToken:navigate',
        url.toString(),
        { handoff: useReadableToken ? 'token' : 'q', replaceHistory: true }
      );
      window.location.replace(url.toString());
    } catch {
      const handoff = readHandoffOrganization();
      if (useReadableToken && authData?.token) {
        const queryString = [
          `token=${encodeURIComponent(authData.token)}`,
          ...(authData.refreshToken
            ? [`refreshToken=${encodeURIComponent(authData.refreshToken)}`]
            : []),
          ...(authData.userName
            ? [`email=${encodeURIComponent(authData.userName)}`]
            : []),
          ...(handoff?.companyId
            ? [`companyId=${encodeURIComponent(handoff.companyId)}`]
            : []),
          ...(handoff?.userId
            ? [`userId=${encodeURIComponent(handoff.userId)}`]
            : []),
        ];
        window.location.replace(`${returnUrl}?${queryString.join('&')}`);
        return;
      }

      const queryString: string[] = [`q=${encodeURIComponent(tokenParam)}`];
      if (machineId) {
        queryString.push(`d=${encodeURIComponent(machineId)}`);
      }
      if (handoff?.companyId) {
        queryString.push(`companyId=${encodeURIComponent(handoff.companyId)}`);
      }
      if (handoff?.userId) {
        queryString.push(`userId=${encodeURIComponent(handoff.userId)}`);
      }
      window.location.replace(`${returnUrl}?${queryString.join('&')}`);
    }
  }

  appBasedRedirection(
    availableProducts: string[],
    token: AuthToken,
    appQueryParam?: string | null
  ){
    if (
      this.wasSignedOutLocally() &&
      !(appQueryParam && this.redirectsWithoutProductLicense(appQueryParam))
    ) {
      return;
    }
    if (appQueryParam && this.redirectsWithoutProductLicense(appQueryParam)) {
      this.clearSignedOutLocally();
    }
    // Deep-link returnUrl takes precedence unless admin/clinic explicitly requested.
    if (
      this.deepLinkReturnUrl &&
      !(appQueryParam && this.redirectsWithoutProductLicense(appQueryParam))
    ) {
      const returnUrl = this.deepLinkReturnUrl;
      this.deepLinkReturnUrl = null;
      void this.redirectToUrlWithTokenPrepared(returnUrl);
      return;
    }

    availableProducts = availableProducts.concat([
      AvailableApplication.Admin,
      AvailableApplication.Phi,
    ]);

    //  Handle single-license case: redirect to Engage
    const filtered = availableProducts.filter(
      (p) => p !== AvailableApplication.Admin && p !== AvailableApplication.Phi
    );

    // If only one license is available, redirect to Engage app directly
    if (filtered.length === 1 && filtered[0] === AvailableApplication.Engage) {
      void this.redirectToUrlWithTokenPrepared(
        this.getUrlByAppName(AvailableApplication.Engage, true)
      );
      return;
    }

    //  End single-license handling
    //  Existing logic follows untouched

    if (appQueryParam) {
      const app = appQueryParam.toLowerCase() as AvailableApplication;
      if (
        this.redirectsWithoutProductLicense(app) ||
        availableProducts.includes(app)
      ) {
        void this.redirectToUrlWithTokenPrepared(this.getUrlByAppName(app, true));
        this.consumePostAuthApp();
        return;
      }
    }
    if (availableProducts.includes(AvailableApplication.Beta)) {
      void this.redirectToUrlWithTokenPrepared(
        this.getUrlByAppName(AvailableApplication.Beta, true)
      );
      return;
    }
    if (availableProducts.includes(AvailableApplication.Studies)) {
      void this.redirectToUrlWithTokenPrepared(
        this.getUrlByAppName(
          AvailableApplication.Studies,
          true
        )
      );
      return;
    }
    if (availableProducts.includes(AvailableApplication.Charts)) {
      if (token.userType === UserType.Biller) {
        void this.redirectToUrlWithTokenPrepared(
          this.getUrlByAppName(
            AvailableApplication.Biller,
            true
          )
        );
        return;
      }
      void this.redirectToUrlWithTokenPrepared(
        this.getUrlByAppName(AvailableApplication.Charts, true)
      );
      return;
    }
    void this.redirectToUrlWithTokenPrepared(
      this.getUrlByAppName(AvailableApplication.Phi, true)
    );
  }

  getUrlByAppName(appName: AvailableApplication, appendRedirectUrl: boolean) {
    let url = '';
    switch (appName) {
      case AvailableApplication.Admin:
        url = environment.adminAppUrl;
        break;
      case AvailableApplication.Remit:
        url = environment.remitAppUrl;
        break;
      case AvailableApplication.Phi:
        url = environment.phiAppUrl;
        break;
      case AvailableApplication.Biller:
        url = environment.billerAppUrl;
        break;
      case AvailableApplication.Studies:
        url = environment.studiesAppUrl;
        break;
      case AvailableApplication.Charts:
        url = environment.chartsAppUrl;
        break;
      case AvailableApplication.Clinic:
        url = environment.clinicAppUrl;
        break;
      case AvailableApplication.HealthPay:
        url = resolveHealthpayAppUrl(this.deepLinkReturnUrl);
        break;
      case AvailableApplication.Automate:
        url = resolveAutomateAppUrl(this.deepLinkReturnUrl);
        break;
      case AvailableApplication.Custodian:
        url = resolveCustodianAppUrl(this.deepLinkReturnUrl);
        break;
      case AvailableApplication.Threads:
        url = resolveThreadsAppUrl(this.deepLinkReturnUrl);
        break;
      case AvailableApplication.ChartsV2:
        url = environment.chartsV2AppUrl;
        break;
      case AvailableApplication.Engage:
        url = environment.engageAppUrl;
        break;
      case AvailableApplication.Signature:
        url = environment.signatureAppUrl;
        break;
      case AvailableApplication.Beta:
        url = environment.betaAppUrl;
        break;
    }
    if (appendRedirectUrl) {
      return url + '/app-redirect';
    }
    return url;
  }

  clearAuthFromLocalStorage() {
    this.clearEntraBffFlow();
    this.stopPeriodicSessionRefresh();
    this.refreshSessionPromise = null;
    const authData = this._localStorageService.authorizationData;
    if (authData?.userName) {
      this.removeAccountFromUserCache(authData.userName);
    }
    this._localStorageService.authorizationData = null;
  }

  private markSignedOutLocally(): void {
    try {
      sessionStorage.setItem(this.signedOutSessionKey, '1');
      localStorage.setItem(this.signedOutLocalStorageKey, '1');
    } catch {
      // ignore
    }
  }

  private clearSignedOutLocally(): void {
    try {
      sessionStorage.removeItem(this.signedOutSessionKey);
      localStorage.removeItem(this.signedOutLocalStorageKey);
    } catch {
      // ignore
    }
  }

  /** True after an explicit logout until the user signs in again. */
  wasSignedOutLocally(): boolean {
    try {
      return (
        sessionStorage.getItem(this.signedOutSessionKey) === '1' ||
        localStorage.getItem(this.signedOutLocalStorageKey) === '1'
      );
    } catch {
      return false;
    }
  }

  getProductNamesWithLicenses(): Observable<string[]> {
    return this.getUserLicenses().pipe(
      map((licenses: CompanyProductLicense[]) =>
        licenses
          //.filter(l => l.name !== 'charts' || (l.name === 'charts' && l.activeBillingPhysicians > 0))
          .map((l) => l.name)
      )
    );
  }

  private getFormattedAddress(geoLocationResponse: any): string {
    let address = '';
    if (!geoLocationResponse) {
      return address;
    }
    if (geoLocationResponse.city) {
      address += geoLocationResponse.city;
    }
    if (geoLocationResponse.postal) {
      address += `, ${geoLocationResponse.postal}`;
    }
    if (geoLocationResponse.region) {
      address += ` ${geoLocationResponse.region}`;
    }
    if (geoLocationResponse.country) {
      address += `, ${geoLocationResponse.country}`;
    }
    return address;
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  get defaultAppUrl(): string {
    return environment.defaultAppUrl;
  }

  get phiAppUrl(): string {
    return environment.phiAppUrl;
  }

  get billerAppUrl(): string {
    return environment.billerAppUrl;
  }

  get isTokenExpired() {
    const authData = this._localStorageService.authorizationData;
    if (!authData?.token) {
      return true;
    }
    if (authData.expiresAt) {
      return new Date(authData.expiresAt) <= new Date();
    }
    return this.isJwtExpired(authData.token);
  }

  get isTokenExpiringSoon(): boolean {
    if (this.isTokenExpired) {
      return true;
    }
    const authData = this._localStorageService.authorizationData;
    if (authData?.expiresAt) {
      return (
        new Date(authData.expiresAt).getTime() - Date.now() <=
        this.tokenRefreshBufferMs
      );
    }
    return this.isJwtExpiringSoon(authData?.token);
  }

  private isJwtExpired(token: string): boolean {
    const payload = this.parseJwtPayload(token);
    const exp = Number(payload?.['exp']);
    if (!exp) {
      return false;
    }
    return exp * 1000 <= Date.now();
  }

  private isJwtExpiringSoon(token?: string): boolean {
    if (!token) {
      return true;
    }
    const payload = this.parseJwtPayload(token);
    const exp = Number(payload?.['exp']);
    if (!exp) {
      return false;
    }
    return exp * 1000 - Date.now() <= this.tokenRefreshBufferMs;
  }

  get storedUserName(): string | null {
    const authData = this._localStorageService.authorizationData;
    if (!authData?.token || !authData?.userName || this.isTokenExpired) {
      return null;
    }
    return authData.userName;
  }

  async getActiveSessionEmail(): Promise<string | null> {
    if (this.wasSignedOutLocally()) {
      return null;
    }

    const localUser = this.storedUserName;
    if (localUser) {
      return localUser;
    }

    if (this.useGoogleEmailOtp()) {
      const firebaseUser = await this._googleFirebaseAuth.getCurrentUser();
      const firebaseEmail = firebaseUser?.email?.trim().toLowerCase();
      if (firebaseEmail) {
        return firebaseEmail;
      }
    }

    const account = await this._entraAuthService.getCurrentAccount();
    if (!account) {
      return null;
    }

    try {
      return (
        account.getAccount?.()?.username ||
        account.getUsername?.() ||
        null
      );
    } catch {
      return null;
    }
  }

  private async startEntraSignIn(
    email: string,
    type: 'SMS' | undefined,
    allowSignupFallback: boolean = true,
    useBff?: boolean,
    retriedAfterEntraSessionClear: boolean = false
  ): Promise<LoginResponse | ValidOTPResponse> {
    const viaBff = useBff ?? this.useEntraEmailOtpBff();
    if (viaBff) {
      return this.startEntraSignInViaBff(email, allowSignupFallback);
    }
    await this.ensureMsalReady();
    const result = await this._entraAuthService.signIn(email);
    // Unknown email on login → create account (same UX as signup).
    if (result.userNotFound) {
      if (allowSignupFallback) {
        return this.continueSignInAfterNativeUserMissing(email, type, viaBff);
      }
      await this.throwIfFederatedEmailKnownInAccounts(email);
      throw {
        error: {
          message: this.noAccountForEmailMessage(),
        },
      };
    }
    if (result.error) {
      if (
        !retriedAfterEntraSessionClear &&
        this.isEntraSessionAlreadyActiveError(result.error)
      ) {
        logGoogleFlow('auth.service:startEntraSignIn:alreadySignedInRecover', {
          email,
        });
        await this.prepareForEmailDeepLink(email);
        await this.ensureMsalReady();
        return this.startEntraSignIn(
          email,
          type,
          allowSignupFallback,
          viaBff,
          true
        );
      }
      if (
        allowSignupFallback &&
        this.isEntraUserAlreadyExistsError(result.error)
      ) {
        return this.startEntraSignIn(email, type, false, viaBff);
      }
      if (
        allowSignupFallback &&
        this.isEntraUserNotFoundError(result.error)
      ) {
        return this.continueSignInAfterNativeUserMissing(email, type, viaBff);
      }
      await this.throwIfFederatedEmailKnownInAccounts(email);
      throw {
        error: {
          message: this.friendlyEmailOtpStartError(result.error),
        },
      };
    }
    this.effectiveAuthMode = 'login';
    return this.mapEntraStepToLoginResponse(result, email, type);
  }

  private async startEntraSignInWithRetries(
    email: string,
    type: 'SMS' | undefined,
    maxAttempts: number
  ): Promise<LoginResponse | ValidOTPResponse> {
    return this.withEntraOtpSendRetries(
      () => this.startEntraSignIn(email, type, true),
      maxAttempts
    );
  }

  private async startEntraSignUpWithRetries(
    email: string,
    maxAttempts: number
  ): Promise<LoginResponse | ValidOTPResponse> {
    return this.withEntraOtpSendRetries(
      () => this.startEntraSignUp(email, true),
      maxAttempts
    );
  }

  private async withEntraOtpSendRetries<T>(
    operation: () => Promise<T>,
    maxAttempts: number = this.entraOtpStartMaxAttempts
  ): Promise<T> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (err) {
        lastError = err;
        if (!this.isRetryableOtpStartError(err) || attempt >= maxAttempts) {
          throw err;
        }
        await this.delayMs(this.entraOtpStartBaseDelayMs * attempt);
      }
    }
    throw lastError;
  }

  /** Deep-link OTP start: retry transient Entra/BFF/network failures only. */
  private isRetryableOtpStartError(err: unknown): boolean {
    const msg = this.apiErrorMessage(err);
    if (this.isRetryableEntraCustomExtensionError(msg)) {
      return true;
    }

    const status = this.apiErrorStatus(err);
    const msgLower = msg.toLowerCase();
    if (
      /registered with google|continue with google|valid email|no account found|user not found|aadsts50034|entra_federated/i.test(
        msgLower
      )
    ) {
      return false;
    }
    if (
      status === 400 ||
      status === 401 ||
      status === 403 ||
      status === 404 ||
      status === 409 ||
      status === 422
    ) {
      return false;
    }
    if (status !== undefined && status >= 500) {
      return true;
    }
    if (
      /unavailable|timeout|network|econnreset|502|503|504|gateway|failed to fetch|http failure/i.test(
        msgLower
      )
    ) {
      return true;
    }
    return false;
  }

  /**
   * Email OTP custom extension failed or timed out — often succeeds on retry.
   * https://learn.microsoft.com/en-us/entra/identity-platform/custom-extension-troubleshoot
   */
  private isRetryableEntraCustomExtensionError(message: string): boolean {
    if (!/aadsts1100001/i.test(message)) {
      return false;
    }
    return /1003005|1003027|1003004|1003001/.test(message);
  }

  private delayMs(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async startEntraSignUp(
    email: string,
    allowLoginFallback: boolean = true,
    useBff?: boolean,
    retriedAfterEntraSessionClear: boolean = false
  ): Promise<LoginResponse | ValidOTPResponse> {
    const viaBff = useBff ?? this.useEntraEmailOtpBff();
    if (viaBff) {
      return this.startEntraSignUpViaBff(email, allowLoginFallback);
    }
    await this.ensureMsalReady();
    const result = await this._entraAuthService.signUp(email, {});
    // Existing email on signup → sign in instead (OTP / password).
    if (
      result.userAlreadyExists ||
      (result.error && this.isEntraUserAlreadyExistsError(result.error))
    ) {
      if (allowLoginFallback) {
        return this.startEntraSignIn(email, undefined, false, viaBff);
      }
      throw {
        error: { message: 'An account with this email already exists.' },
      };
    }
    if (result.error) {
      if (
        !retriedAfterEntraSessionClear &&
        this.isEntraSessionAlreadyActiveError(result.error)
      ) {
        logGoogleFlow('auth.service:startEntraSignUp:alreadySignedInRecover', {
          email,
        });
        await this.prepareForEmailDeepLink(email);
        await this.ensureMsalReady();
        return this.startEntraSignUp(
          email,
          allowLoginFallback,
          viaBff,
          true
        );
      }
      throw {
        error: {
          message: this.friendlyEmailOtpStartError(result.error),
        },
      };
    }
    this.effectiveAuthMode = 'signup';
    return this.mapEntraStepToLoginResponse(result, email);
  }

  private noAccountForEmailMessage(): string {
    return FEDERATED_EMAIL_OTP_ROUTING_ENABLED
      ? 'No account found for this email. Sign up with email or use Continue with Google.'
      : 'No account found for this email. Try signing up with email.';
  }

  private federatedEmailOtpUnavailableMessage(): string {
    return (
      'This email is registered with Google (or another social sign-in). ' +
      'Use Continue with Google for this address. ' +
      'To use email one-time passcode, sign up with email first, then link Google from your account.'
    );
  }

  /**
   * Accounts DB only (POST login/entra/resolve-email).
   */
  async resolveEmailAccountsLink(email: string): Promise<{
    entraObjectId: string | null;
    accountsEntraLinked: boolean;
    signUpAuthMethod: 'email' | 'google' | 'apple' | null;
  }> {
    return this.fetchAccountsEntraLinkForEmail(email);
  }

  /**
   * Accounts DB only (POST login/entra/resolve-email) — same check as BFF login/start after AADSTS50034.
   */
  private async fetchAccountsEntraLinkForEmail(email: string): Promise<{
    entraObjectId: string | null;
    accountsEntraLinked: boolean;
    signUpAuthMethod: 'email' | 'google' | 'apple' | null;
  }> {
    const normalized = email.trim().toLowerCase();
    try {
      const res = await firstValueFrom(
        this._dataService.post('api/v1/auth/login/entra/resolve-email', {
          email: normalized,
        })
      );
      const oid =
        typeof res?.entraObjectId === 'string' ? res.entraObjectId.trim() : '';
      const method = res?.signUpAuthMethod;
      const signUpAuthMethod =
        method === 'email' || method === 'google' || method === 'apple'
          ? method
          : null;
      return {
        entraObjectId: oid || null,
        accountsEntraLinked: !!res?.accountsEntraLinked || !!oid,
        signUpAuthMethod,
      };
    } catch {
      return {
        entraObjectId: null,
        accountsEntraLinked: false,
        signUpAuthMethod: null,
      };
    }
  }

  /** Entra native sign-in missing + Accounts has entraObjectId → federated email OTP unavailable. */
  private async throwIfFederatedEmailKnownInAccounts(
    email: string
  ): Promise<void> {
    if (!FEDERATED_EMAIL_OTP_ROUTING_ENABLED) {
      return;
    }
    const { accountsEntraLinked } =
      await this.fetchAccountsEntraLinkForEmail(email);
    if (accountsEntraLinked) {
      throw {
        error: {
          message: this.federatedEmailOtpUnavailableMessage(),
          code: 'ENTRA_FEDERATED_EMAIL_OTP_UNAVAILABLE',
        },
        status: 400,
      };
    }
  }

  private async continueSignInAfterNativeUserMissing(
    email: string,
    type: 'SMS' | undefined,
    viaBff: boolean
  ): Promise<LoginResponse | ValidOTPResponse> {
    await this.throwIfFederatedEmailKnownInAccounts(email);
    return this.startEntraSignUp(email, true, viaBff);
  }

  private friendlyEmailOtpStartError(message: string): string {
    const resolved = this.friendlyBffEntraError(message);
    if (this.isEntraUserNotFoundError(message)) {
      return this.noAccountForEmailMessage();
    }
    if (
      FEDERATED_EMAIL_OTP_ROUTING_ENABLED &&
      /native authentication is not available|federated|redirect required|another identity provider|aadsts50076|aadsts50105/i.test(
        message
      )
    ) {
      return this.federatedEmailOtpUnavailableMessage();
    }
    return resolved;
  }

  private async continueEntraSignUpPassword(
    password: string,
    email: string
  ): Promise<ValidOTPResponse | LoginResponse> {
    const result = await this._entraAuthService.submitSignUpPassword(password);
    if (result.error) {
      throw { error: { message: result.error } };
    }
    if (result.step === 'completed' && result.accountData) {
      return this.toValidOtpFromAccount(result.accountData, email);
    }
    if (result.step === 'code' || result.step === 'password') {
      return this.mapEntraStepToLoginResponse(result, email);
    }
    if (result.step === 'mfa-methods' && result.authMethods?.length) {
      const mfaResult = await this._entraAuthService.requestMfaChallenge(
        result.authMethods[0].id
      );
      if (mfaResult.error) {
        throw { error: { message: mfaResult.error } };
      }
      if (mfaResult.step === 'completed' && mfaResult.accountData) {
        return this.toValidOtpFromAccount(mfaResult.accountData, email);
      }
      return this.mapEntraStepToLoginResponse(
        { ...mfaResult, step: 'code' },
        email
      );
    }
    if (result.step === 'mfa-challenge') {
      return this.mapEntraStepToLoginResponse(
        { ...result, step: 'code' },
        email
      );
    }
    throw { error: { message: 'Unexpected authentication response.' } };
  }

  private async continueEntraWithCode(
    otp: string,
    email: string
  ): Promise<ValidOTPResponse | LoginResponse> {
    if (this.entraEmailFlowToken && this.entraEmailFlowKind) {
      return this.continueEntraWithCodeViaBff(otp, email);
    }
    const result = await this._entraAuthService.submitCode(otp);
    if (result.error) {
      throw { error: { message: result.error, messages: [result.error] } };
    }
    if (result.step === 'completed' && result.accountData) {
      return this.toValidOtpFromAccount(result.accountData, email);
    }
    if (result.step === 'auth-method-registration') {
      return this.mapEntraStepToLoginResponse(result, email);
    }
    if (result.step === 'auth-method-challenge' || result.step === 'mfa-challenge') {
      return this.mapEntraStepToLoginResponse(result, email);
    }
    if (result.step === 'mfa-methods' && result.authMethods?.length) {
      const smsMethod = this._entraAuthService.pickSmsAuthMethod(
        result.authMethods
      );
      if (!smsMethod) {
        throw { error: { message: 'SMS verification is not available.' } };
      }
      const mfaResult = await this._entraAuthService.requestMfaChallenge(
        smsMethod.id
      );
      if (mfaResult.error) {
        throw { error: { message: mfaResult.error } };
      }
      if (mfaResult.step === 'completed' && mfaResult.accountData) {
        return this.toValidOtpFromAccount(mfaResult.accountData, email);
      }
      return this.mapEntraStepToLoginResponse(
        { ...mfaResult, step: 'mfa-challenge' },
        email
      );
    }
    throw { error: { message: 'Unexpected authentication response.' } };
  }

  private async continueEntraRegisterPhone(
    phone: string,
    email: string
  ): Promise<ValidOTPResponse | LoginResponse> {
    const methods = this._entraAuthService.getRegistrationAuthMethods();
    const smsMethod = this._entraAuthService.pickSmsAuthMethod(methods);
    if (!smsMethod) {
      throw {
        error: {
          message:
            'SMS two-factor authentication is not available for this account.',
        },
      };
    }

    const normalizedPhone = Utils.normalizePhoneForEntra(phone);
    const result = await this._entraAuthService.challengeAuthMethod(
      smsMethod,
      normalizedPhone
    );
    if (result.error) {
      throw { error: { message: this.friendlyEntraPhoneError(result.error) } };
    }
    if (result.step === 'completed' && result.accountData) {
      return this.toValidOtpFromAccount(result.accountData, email);
    }
    if (result.step === 'auth-method-challenge') {
      return {
        email,
        phone: this.maskPhone(normalizedPhone),
        status: 'MfaCode',
      };
    }
    throw { error: { message: 'Unexpected authentication response.' } };
  }

  private async continueEntraWithPassword(
    password: string,
    email: string
  ): Promise<ValidOTPResponse | LoginResponse> {
    const result = await this._entraAuthService.submitPassword(password);
    if (result.error) {
      throw { error: { message: result.error } };
    }
    if (result.step === 'completed' && result.accountData) {
      return this.toValidOtpFromAccount(result.accountData, email);
    }
    if (result.step === 'code') {
      return this.mapEntraStepToLoginResponse(result, email);
    }
    if (result.step === 'mfa-methods' && result.authMethods?.length) {
      const smsMethod = this._entraAuthService.pickSmsAuthMethod(
        result.authMethods
      );
      if (!smsMethod) {
        throw { error: { message: 'SMS verification is not available.' } };
      }
      const mfaResult = await this._entraAuthService.requestMfaChallenge(
        smsMethod.id
      );
      if (mfaResult.error) {
        throw { error: { message: mfaResult.error } };
      }
      if (mfaResult.step === 'completed' && mfaResult.accountData) {
        return this.toValidOtpFromAccount(mfaResult.accountData, email);
      }
      return this.mapEntraStepToLoginResponse(
        { ...mfaResult, step: 'mfa-challenge' },
        email
      );
    }
    if (result.step === 'mfa-challenge') {
      return this.mapEntraStepToLoginResponse(result, email);
    }
    if (result.step === 'auth-method-registration') {
      return this.mapEntraStepToLoginResponse(result, email);
    }
    throw { error: { message: 'Unexpected authentication response.' } };
  }

  private async resendEntraCode(): Promise<LoginResponse | ValidOTPResponse> {
    if (this.useGooglePasswordlessLinkFlow()) {
      return this.resendGooglePasswordlessEmailLink();
    }
    if (this.useGoogleEmailOtp()) {
      return this.resendGoogleCodeViaBff();
    }
    if (this.entraEmailFlowToken && this.entraEmailFlowKind) {
      return this.resendEntraCodeViaBff();
    }
    await this.ensureMsalReady();
    return this.withEntraOtpSendRetries(async () => {
      const result = await this._entraAuthService.resendCode();
      if (result.error) {
        throw {
          error: { message: result.error },
          status: 400,
        };
      }
      return {
        email: '',
        phone: '',
        status: 'SMS' as const,
      };
    });
  }

  private async mapEntraStepToLoginResponse(
    result: EntraFlowResult,
    email: string,
    type: 'SMS' | undefined = undefined
  ): Promise<LoginResponse | ValidOTPResponse> {
    if (result.step === 'completed' && result.accountData) {
      return this.toValidOtpFromAccount(result.accountData, email);
    }
    if (result.step === 'password') {
      return {
        email,
        phone: '',
        status: 'Password',
      };
    }
    if (result.step === 'code') {
      return {
        email,
        phone: this.maskEmail(email),
        status: 'SMS',
      };
    }
    if (result.step === 'mfa-challenge') {
      return {
        email,
        phone: '',
        status: 'MfaCode',
      };
    }
    if (result.step === 'auth-method-registration') {
      return {
        email,
        phone: '',
        status: 'PhoneRegistration',
      };
    }
    if (result.step === 'auth-method-challenge') {
      return {
        email,
        phone: '',
        status: 'MfaCode',
      };
    }
    if (result.step === 'mfa-methods' && result.authMethods?.length) {
      const smsMethod = this._entraAuthService.pickSmsAuthMethod(
        result.authMethods
      );
      if (!smsMethod) {
        throw { error: { message: 'SMS verification is not available.' } };
      }
      const mfaResult = await this._entraAuthService.requestMfaChallenge(
        smsMethod.id
      );
      if (mfaResult.error) {
        throw { error: { message: mfaResult.error } };
      }
      if (mfaResult.step === 'completed' && mfaResult.accountData) {
        return this.toValidOtpFromAccount(mfaResult.accountData, email);
      }
      return {
        email,
        phone: this.maskEmail(email),
        status: 'MfaCode',
      };
    }
    throw {
      error: {
        message: result.error || 'Unexpected authentication response.',
      },
    };
  }

  private friendlyEntraPhoneError(message: string): string {
    if (/399293|phone region has been blocked/i.test(message)) {
      return 'SMS to this country is blocked in Azure by default. Enable country code 92 (Pakistan) in Entra, or test with a US number (+1 5555551234).';
    }
    if (/901010|challenge_target|invalid.*phone/i.test(message)) {
      return 'Enter your number with a space after the country code, e.g. +92 3155432768 or +1 5555551234.';
    }
    return message;
  }

  private maskPhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 4) {
      return phone;
    }
    return `***${digits.slice(-4)}`;
  }

  /**
   * Called after Entra email OTP (or MFA) verification succeeds and tokens exist.
   * Provisions/links the Accounts user for both signup and login (API is idempotent).
   */
  private async toValidOtpFromAccount(
    accountData: CustomAuthAccountData,
    email: string
  ): Promise<ValidOTPResponse | LoginResponse> {
    const token = await this.buildAuthTokenFromEntra(accountData);
    const resolvedEmail = email || accountData.getAccount()?.username || '';
    this.upsertUserCache(resolvedEmail);

    const accessToken = token.accessToken?.trim() || '';
    if (!accessToken) {
      logGoogleFlow('auth.service:toValidOtpFromAccount:noIdToken');
      throw {
        error: {
          message:
            'Sign-in with Google finished in Entra, but no ID token was available to set up your Health Exam account. Please try Continue with Google again.',
        },
      };
    }

    await this.provisionAccountsUser(resolvedEmail, accessToken);

    return {
      status: 'Token',
      response: token,
      userId: accountData.getAccount()?.localAccountId || '',
    };
  }

  /**
   * POST /api/v1/auth/identity/register with Entra Bearer.
   * Creates/links Accounts user + sets entraObjectId; attaches default org membership.
   * Does not clear the Entra session on failure (caller can retry).
   */
  async provisionAccountsUser(email: string, accessToken: string): Promise<void> {
    const registerEmail = this.resolveRegistrationEmail(email, accessToken);
    const names = this.resolveRegisterNames(registerEmail, accessToken);
    const organizationId = environment.defaultSignupOrganizationId;
    const signupSource = this.resolveRegisterSignupSource();

    try {
      const identity = await firstValueFrom(
        this._dataService.post(
          'api/v1/auth/identity/register',
          {
            email: registerEmail,
            firstName: names.firstName,
            lastName: names.lastName,
            organizationId,
            signUpAuthMethod: this.registerSignUpAuthMethod,
            ...(signupSource ? { signupSource } : {}),
          },
          accessToken
        )
      );
      this.lastAccountsIdentity = identity;
      this.consumeRegisterSignupSource();
    } catch (err: any) {
      const status = err?.status ?? err?.error?.statusCode;
      const apiMessage =
        err?.error?.message ||
        (typeof err?.error === 'string' ? err.error : undefined);
      let message =
        apiMessage ||
        'Could not complete account setup. Please try again.';

      if (status === 409) {
        message =
          apiMessage ||
          'This email is already linked to a different Microsoft identity. Sign in with the original account, or use a different email.';
      }

      console.error('Accounts identity/register failed', err);
      // Keep Entra session; surface error so UI can retry without signing out.
      throw { error: { message }, status };
    }
  }

  /** Prefer JWT mailbox over CIAM `{oid}@tenant.onmicrosoft.com` username. */
  private resolveRegistrationEmail(
    fallback: string,
    idToken: string
  ): string {
    const claims = this.parseJwtPayload(idToken);
    const candidates = [
      claims?.['email'] as string | undefined,
      claims?.['preferred_username'] as string | undefined,
      fallback,
    ];
    const normalized = candidates
      .filter((c): c is string => typeof c === 'string' && !!c.trim())
      .map((c) => c.trim().toLowerCase());
    const preferred = normalized.find(
      (c) => !this.isSyntheticEntraPrincipalEmail(c)
    );
    return preferred || normalized[0] || '';
  }

  private isSyntheticEntraPrincipalEmail(email: string): boolean {
    const normalized = email.trim().toLowerCase();
    const at = normalized.indexOf('@');
    if (at <= 0) {
      return false;
    }
    const local = normalized.slice(0, at);
    const domain = normalized.slice(at + 1);
    if (!domain.endsWith('.onmicrosoft.com')) {
      return false;
    }
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      local
    );
  }

  /** Signup must not pre-fill profile names from IdP claims — collected in Aya onboarding. */
  private resolveRegisterNames(
    _email: string,
    _idToken: string
  ): { firstName: string; lastName: string } {
    return { firstName: '', lastName: '' };
  }

  private isLikelyEmailLocalPartName(name: string, email: string): boolean {
    const n = this.sanitizeUserName(name);
    if (!n) {
      return false;
    }
    const normalized = email.trim().toLowerCase();
    const at = normalized.indexOf('@');
    const local = at > 0 ? normalized.slice(0, at) : normalized;
    if (!local) {
      return false;
    }
    const lower = n.toLowerCase();
    if (lower === local) {
      return true;
    }
    const firstSeg = local.split(/[._+\-]/)[0];
    return !!firstSeg && lower === firstSeg;
  }

  private sanitizeUserName(value: string): string {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return '';
    }
    const normalized = trimmed.replace(/\s+/g, ' ').toLowerCase();
    if (
      normalized === '.' ||
      normalized === 'unknown' ||
      normalized === 'unknown.' ||
      normalized === 'unknown .' ||
      /^[.\s]+$/.test(trimmed)
    ) {
      return '';
    }
    return trimmed.replace(/\s+/g, ' ');
  }

  /**
   * Build the Bearer token stored for Accounts API calls.
   * Backend ENTRA_AUDIENCE is the native SPA client id, so we must send the Entra ID token.
   * Do not call getAccessToken({ scopes: [] }) — that returns a Microsoft Graph token.
   */
  private async buildAuthTokenFromEntra(
    accountData: CustomAuthAccountData
  ): Promise<AuthToken> {
    let idToken = accountData.getIdToken?.() || '';
    if (!idToken) {
      idToken = this._entraAuthService.getStashedNativeIdToken();
    }
    if (!idToken) {
      try {
        const refreshed = await this._entraAuthService.refreshAccountTokens(false);
        idToken = refreshed?.idToken || accountData.getIdToken?.() || '';
      } catch (err) {
        console.warn('[auth] Could not hydrate Entra ID token after sign-in', err);
      }
    }
    if (!idToken) {
      return { accessToken: '', refreshToken: '' };
    }

    if (!this.isAccountsApiCompatibleToken(idToken)) {
      console.warn(
        'Entra ID token audience is not compatible with Accounts API; storing anyway as fallback.'
      );
    }

    const existingRefresh =
      this._localStorageService.authorizationData?.refreshToken?.trim() || '';
    let refreshToken =
      existingRefresh ||
      (await this._entraAuthService.resolveEntraRefreshToken(accountData));

    if (!refreshToken) {
      try {
        await this._entraAuthService.refreshAccountTokens(true);
        refreshToken =
          (await this._entraAuthService.resolveEntraRefreshToken(accountData)) ||
          existingRefresh;
      } catch (err) {
        console.warn('[auth] Could not force Entra refresh while resolving refresh token', err);
      }
    }

    if (!refreshToken) {
      console.warn(
        '[auth] No Entra refresh token after native sign-in; SPA session/refresh will not work until re-login with offline_access.'
      );
    }

    return {
      accessToken: idToken,
      refreshToken,
    };
  }

  /** Reject Microsoft Graph tokens; accept Entra native-client or Firebase ID tokens. */
  private isAccountsApiCompatibleToken(token: string): boolean {
    const payload = this.parseJwtPayload(token);
    if (!payload) {
      return false;
    }
    const aud = payload['aud'];
    const audiences = Array.isArray(aud) ? aud : [aud];
    if (audiences.includes(MICROSOFT_GRAPH_AUDIENCE)) {
      return false;
    }
    if (audiences.includes(ENTRA_NATIVE_CLIENT_ID)) {
      return true;
    }
    const projectId = environment.firebase?.projectId?.trim();
    const iss = String(payload['iss'] || '');
    if (
      projectId &&
      iss === `https://securetoken.google.com/${projectId}` &&
      audiences.includes(projectId)
    ) {
      return true;
    }
    return false;
  }

  private clearEntraBffFlow(): void {
    this.entraEmailFlowToken = null;
    this.entraEmailFlowKind = null;
    this.googlePasswordlessLinkSession = false;
  }

  /** User-facing text from Accounts API / HttpErrorResponse (login, register, BFF). */
  resolveApiError(err: unknown): string {
    return this.friendlyBffEntraError(this.apiErrorMessage(err));
  }

  /** Accounts geofence middleware/guard — show region-unavailable page instead of inline error. */
  isGeofenceDeniedError(err: unknown): boolean {
    return isGeofenceDeniedApiError(err);
  }

  /**
   * Page-load geofence probe (GET auth/geofence/check).
   * Returns false when the region is blocked; true when allowed or check unavailable.
   */
  async probeGeofenceAccess(): Promise<boolean> {
    const base = environment.apiBaseUrl.replace(/\/?$/, '/');
    const url = `${base}api/v1/auth/geofence/check`;
    try {
      const response = await fetch(url, { method: 'GET' });
      if (response.status !== 403) {
        return true;
      }
      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }
      const err = { status: 403, error: body };
      return !this.isGeofenceDeniedError(err);
    } catch {
      return true;
    }
  }

  /** BFF login/entra/start: email exists as Google (or other social) in Entra. */
  isFederatedEmailOtpUnavailableError(err: unknown): boolean {
    if (!FEDERATED_EMAIL_OTP_ROUTING_ENABLED) {
      return false;
    }
    const anyErr = err as {
      error?:
        | string
        | { message?: string; messages?: string; code?: string };
    };
    const body = anyErr?.error;
    if (body && typeof body === 'object') {
      if (body.code === 'ENTRA_FEDERATED_EMAIL_OTP_UNAVAILABLE') {
        return true;
      }
    }
    const msg = this.apiErrorMessage(err);
    return /registered with Google|continue with Google for this address|entra_federated_email_otp_unavailable/i.test(
      msg
    );
  }

  private apiErrorMessage(err: unknown): string {
    const anyErr = err as {
      error?:
        | string
        | {
            message?: string | string[];
            messages?: string | string[];
          };
      message?: string;
      status?: number;
    };

    const body = anyErr?.error;
    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }
    if (body && typeof body === 'object') {
      const fromMessage = this.coerceErrorText(body.message);
      if (fromMessage) {
        return fromMessage;
      }
      const fromMessages = this.coerceErrorText(body.messages);
      if (fromMessages) {
        return fromMessages;
      }
    }

    const wrapped = (err as { error?: { message?: string } })?.error?.message;
    if (typeof wrapped === 'string' && wrapped.trim()) {
      return wrapped.trim();
    }

    const httpMsg = anyErr?.message;
    if (
      typeof httpMsg === 'string' &&
      httpMsg.trim() &&
      !/^Http failure response for/i.test(httpMsg)
    ) {
      return httpMsg.trim();
    }

    return 'Request failed';
  }

  private coerceErrorText(value: unknown): string | null {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) {
          return item.trim();
        }
      }
    }
    return null;
  }

  private apiErrorStatus(err: unknown): number | undefined {
    const anyErr = err as { status?: number; error?: { statusCode?: number } };
    return anyErr?.status ?? anyErr?.error?.statusCode;
  }

  private friendlyBffEntraError(message: string): string {
    if (
      FEDERATED_EMAIL_OTP_ROUTING_ENABLED &&
      /registered with Google|Continue with Google for this address/i.test(message)
    ) {
      return message;
    }
    if (
      !FEDERATED_EMAIL_OTP_ROUTING_ENABLED &&
      /registered with Google|Continue with Google|entra_federated_email_otp_unavailable/i.test(
        message
      )
    ) {
      return 'We could not start email sign-in for this address. Please try again.';
    }
    if (/no account found for this email/i.test(message)) {
      return message;
    }
    if (/challenge type:\s*redirect|browser redirect fallback/i.test(message)) {
      return (
        'Email sign-in needs the hosted login page for this account (MFA or registration). ' +
        'Try again in a private window, or ask an admin to adjust Conditional Access for email OTP.'
      );
    }
    return message;
  }

  private isEntraUserNotFoundError(message: string): boolean {
    return /user not found|doesn't exist|does not exist|unknown user|invalid username|aadsts50034|no account/i.test(
      message
    );
  }

  /** Native Auth sign-in while MSAL still holds an Entra session on this origin. */
  private isEntraSessionAlreadyActiveError(message: string): boolean {
    return /already signed in|user has already signed/i.test(message.trim());
  }

  /** Sign-up start: account exists (local or federated) → switch to sign-in. */
  private isEntraUserAlreadyExistsError(message: string): boolean {
    return /user_already_exists|aadsts1003037|\b1003037\b|already have an account with us|may already have an account/i.test(
      message
    );
  }

  private isAccountsEmailTakenError(err: unknown, message: string): boolean {
    if (this.apiErrorStatus(err) === 409) {
      return true;
    }
    return (
      this.isEntraUserAlreadyExistsError(message) ||
      /already registered|already exists|email.*in use|conflict/i.test(message)
    );
  }

  private async startEntraSignInViaBff(
    email: string,
    allowSignupFallback: boolean
  ): Promise<LoginResponse | ValidOTPResponse> {
    const normalized = email.trim().toLowerCase();
    try {
      const res = await firstValueFrom(
        this._dataService.post('api/v1/auth/login/entra/start', {
          email: normalized,
        })
      );
      this.entraEmailFlowToken = String(res.flowToken || '');
      this.entraEmailFlowKind = 'login';
      this.effectiveAuthMode = 'login';
      return {
        email: normalized,
        phone: this.maskEmail(normalized),
        status: 'SMS',
      };
    } catch (err: unknown) {
      const message = this.apiErrorMessage(err);
      if (allowSignupFallback && this.isEntraUserNotFoundError(message)) {
        await this.throwIfFederatedEmailKnownInAccounts(normalized);
        return this.startEntraSignUpViaBff(normalized, true);
      }
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async startEntraSignUpViaBff(
    email: string,
    allowLoginFallback: boolean
  ): Promise<LoginResponse | ValidOTPResponse> {
    const normalized = email.trim().toLowerCase();
    try {
      const res = await firstValueFrom(
        this._dataService.post('api/v1/auth/register/entra/start', {
          email: normalized,
        })
      );
      this.entraEmailFlowToken = String(res.flowToken || '');
      this.entraEmailFlowKind = 'register';
      this.effectiveAuthMode = 'signup';
      return {
        email: normalized,
        phone: this.maskEmail(normalized),
        status: 'SMS',
      };
    } catch (err: unknown) {
      const message = this.apiErrorMessage(err);
      if (
        allowLoginFallback &&
        this.isAccountsEmailTakenError(err, message)
      ) {
        return this.startEntraSignInViaBff(normalized, false);
      }
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async continueEntraWithCodeViaBff(
    otp: string,
    email: string
  ): Promise<ValidOTPResponse | LoginResponse> {
    const flowToken = this.entraEmailFlowToken;
    const kind = this.entraEmailFlowKind;
    if (!flowToken || !kind) {
      throw { error: { message: 'Sign-in session expired. Please start again.' } };
    }

    const code = otp.trim();
    try {
      if (kind === 'login') {
        const res = await firstValueFrom(
          this._dataService.post('api/v1/auth/login/entra/verify', {
            flowToken,
            code,
          })
        );
        return this.finishBffEntraAuth(res, email);
      }

      const verifyRes = await firstValueFrom(
        this._dataService.post('api/v1/auth/register/entra/verify', {
          flowToken,
          code,
        })
      );
      return this.completeEntraRegisterViaBff(
        email,
        String(verifyRes.flowToken || flowToken || '')
      );
    } catch (err: unknown) {
      const message = this.apiErrorMessage(err);
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async resendEntraCodeViaBff(): Promise<LoginResponse> {
    const flowToken = this.entraEmailFlowToken;
    const kind = this.entraEmailFlowKind;
    if (!flowToken || !kind) {
      throw { error: { message: 'Sign-in session expired. Please start again.' } };
    }

    const path =
      kind === 'login'
        ? 'api/v1/auth/login/entra/resend'
        : 'api/v1/auth/register/entra/resend';
    const res = await this.withEntraOtpSendRetries(async () => {
      try {
        return await firstValueFrom(
          this._dataService.post(path, { flowToken })
        );
      } catch (err: unknown) {
        throw {
          error: { message: this.resolveApiError(err) },
          status: this.apiErrorStatus(err),
        };
      }
    });
    if (res.flowToken) {
      this.entraEmailFlowToken = String(res.flowToken);
    }
    return {
      email: '',
      phone: '',
      status: 'SMS',
    };
  }

  /** Step 3 — finish Entra signup + Accounts user after email OTP verify. */
  private async completeEntraRegisterViaBff(
    email: string,
    flowToken?: string
  ): Promise<ValidOTPResponse> {
    const token = (flowToken || this.entraEmailFlowToken || '').trim();
    if (!token) {
      throw {
        error: {
          message: 'Registration session expired. Please start again.',
        },
      };
    }
    const names = this.resolveRegisterNames(email, '');
    const completeRes = await firstValueFrom(
      this._dataService.post('api/v1/auth/register/entra/complete', {
        flowToken: token,
        firstName: names.firstName,
        lastName: names.lastName,
      })
    );
    return this.finishBffEntraAuth(completeRes, email);
  }

  private async finishBffEntraAuth(
    response: Record<string, unknown>,
    email: string
  ): Promise<ValidOTPResponse> {
    const tokens = (response['tokens'] || {}) as {
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
    };
    const idToken = (tokens.idToken || tokens.accessToken || '').trim();
    let refreshToken = (tokens.refreshToken || '').trim();
    const resolvedEmail = email.trim().toLowerCase();

    if (!refreshToken) {
      refreshToken =
        (await this._entraAuthService.resolveEntraRefreshToken()) || '';
    }
    if (!refreshToken) {
      console.warn(
        '[auth] BFF Entra verify succeeded but no refreshToken in response; SPA session refresh may fail.'
      );
    }

    this.clearEntraBffFlow();
    this.clearSignedOutLocally();
    this.persistAuthData(resolvedEmail, {
      accessToken: idToken,
      refreshToken,
    });
    this.lastAccountsIdentity = response;
    this.upsertUserCache(resolvedEmail);

    const user = response['user'] as { id?: string } | undefined;
    return {
      status: 'Token',
      response: {
        accessToken: idToken,
        refreshToken,
      },
      userId: user?.id ? String(user.id) : '',
    };
  }

  private maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    if (!local || !domain) {
      return email;
    }
    const visible = local.slice(0, Math.min(2, local.length));
    return `${visible}***@${domain}`;
  }

  // --- Google Identity Platform email OTP (Accounts BFF + Firebase) ---

  private passwordlessEmailContinueUrl(email?: string): string {
    return resolvePasswordlessEmailContinueUrl(email);
  }

  private shouldIncludePasswordlessContinueUrl(
    passwordlessLink = false
  ): boolean {
    return (
      this.useFirebasePasswordlessEmailAuth() ||
      passwordlessLink ||
      this.googlePasswordlessLinkSession
    );
  }

  private async requestGooglePasswordlessEmailLink(
    email: string
  ): Promise<LoginResponse | ValidOTPResponse> {
    if (!this.canOfferGooglePasswordlessLinkFallback()) {
      throw {
        error: { message: 'Email link sign-in is not available.' },
      };
    }

    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      throw {
        error: { message: 'Enter your email to receive a sign-in link.' },
      };
    }

    this.googlePasswordlessLinkSession = true;
    this._googleFirebaseAuth.storeEmailForSignInLink(normalized);

    if (this.entraEmailFlowToken) {
      return this.resendGooglePasswordlessEmailLink(normalized);
    }

    if (this.effectiveAuthMode === 'signup') {
      return this.startGoogleSignUpViaBff(normalized, false, true);
    }
    return this.startGoogleSignInViaBff(normalized, false, true);
  }

  /**
   * Handle Firebase email action links redirected from /__/auth/action → /login.
   * Returns true when verifyEmail (or similar) was applied.
   */
  async tryApplyFirebaseEmailActionFromUrl(): Promise<boolean> {
    if (!this.useGoogleEmailOtp()) {
      return false;
    }
    const action = this._googleFirebaseAuth.readFirebaseEmailActionFromUrl();
    if (!action) {
      return false;
    }
    if (action.mode === 'signIn') {
      return false;
    }
    if (action.mode === 'verifyEmail') {
      await this._googleFirebaseAuth.applyEmailVerificationCode(action.oobCode);
      this._googleFirebaseAuth.clearFirebaseEmailActionQueryParams();
      return true;
    }
    return false;
  }

  /**
   * Complete Firebase passwordless email link sign-in when the user returns to /login.
   * Returns null when the current URL is not an email link callback.
   */
  async tryCompleteFirebaseEmailLinkSignIn(
    emailOverride?: string
  ): Promise<ValidOTPResponse | null> {
    if (!this.useGoogleEmailOtp()) {
      return null;
    }
    if (!this._googleFirebaseAuth.isEmailLinkSignInReturn()) {
      return null;
    }

    try {
      const action = this._googleFirebaseAuth.readFirebaseEmailActionFromUrl();
      const email =
        emailOverride?.trim().toLowerCase() ||
        this._googleFirebaseAuth.readEmailFromSignInLinkUrl() ||
        this._googleFirebaseAuth.readStoredEmailForSignInLink() ||
        '';
      const flowToken = this._googleFirebaseAuth.readFlowTokenFromSignInLinkUrl();
      const oobCode = action?.oobCode?.trim() || '';

      if (flowToken && oobCode && email && action?.mode === 'signIn') {
        return this.completePasswordlessEmailLinkViaBff({
          email,
          oobCode,
          flowToken,
          flowKind: this._googleFirebaseAuth.readFlowKindFromSignInLinkUrl(),
        });
      }

      const cred = await this._googleFirebaseAuth.completeEmailLinkSignIn(
        emailOverride
      );
      if (!cred?.user) {
        return null;
      }

      const firebaseUser = cred.user;
      const { idToken, refreshToken } =
        await this._googleFirebaseAuth.resolveSessionTokens(firebaseUser);
      const resolvedEmail =
        firebaseUser.email?.trim().toLowerCase() || email;

      if (!resolvedEmail) {
        throw {
          error: {
            message:
              'Sign-in succeeded but no email was returned. Try again or use another method.',
          },
        };
      }

      this.registerSignUpAuthMethod = 'email';
      await this.provisionAccountsUser(resolvedEmail, idToken);
      this.clearEntraBffFlow();
      this.clearSignedOutLocally();
      this.clearFirebaseSocialSignInMarker();
      this.persistAuthData(resolvedEmail, { accessToken: idToken, refreshToken });
      this.upsertUserCache(resolvedEmail);

      const registeredUser = (
        this.lastAccountsIdentity as Record<string, unknown> | null
      )?.['user'] as { id?: string } | undefined;

      this._googleFirebaseAuth.clearFirebaseEmailActionQueryParams();

      return {
        status: 'Token',
        response: { accessToken: idToken, refreshToken },
        userId: registeredUser?.id ? String(registeredUser.id) : '',
      };
    } catch (err: unknown) {
      throw {
        error: {
          message: this.resolveApiError(err),
        },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async completePasswordlessEmailLinkViaBff(options: {
    email: string;
    oobCode: string;
    flowToken: string;
    flowKind: 'login' | 'register';
  }): Promise<ValidOTPResponse> {
    const path =
      options.flowKind === 'register'
        ? 'api/v1/auth/register/google/passwordless/complete'
        : 'api/v1/auth/login/google/passwordless/complete';

    const res = await firstValueFrom(
      this._dataService.post(path, {
        email: options.email,
        oobCode: options.oobCode,
        flowToken: options.flowToken,
      })
    );

    this._googleFirebaseAuth.clearFirebaseEmailActionQueryParams();

    return this.finishGoogleBffAuth(res, options.email);
  }

  private async startGooglePasswordlessEmailLink(
    email: string,
    allowFallback: boolean,
    mode: 'login' | 'signup' = 'login'
  ): Promise<LoginResponse | ValidOTPResponse> {
    const normalized = email.trim().toLowerCase();
    try {
      if (mode === 'signup') {
        return this.startGoogleSignUpViaBff(normalized, allowFallback);
      }
      return this.startGoogleSignInViaBff(normalized, allowFallback);
    } catch (err: unknown) {
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async resendGooglePasswordlessEmailLink(
    emailOverride?: string
  ): Promise<LoginResponse | ValidOTPResponse> {
    this.googlePasswordlessLinkSession = true;
    const email =
      emailOverride?.trim().toLowerCase() ||
      this._googleFirebaseAuth.readStoredEmailForSignInLink() ||
      this._localStorageService.authorizationData?.userName ||
      '';
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      throw {
        error: {
          message: 'Enter your email again to resend the sign-in link.',
        },
      };
    }

    const flowToken = this.entraEmailFlowToken;
    if (!flowToken) {
      if (this.effectiveAuthMode === 'signup') {
        return this.startGoogleSignUpViaBff(normalized, false);
      }
      return this.startGoogleSignInViaBff(normalized, false);
    }

    try {
      const path =
        this.entraEmailFlowKind === 'register'
          ? 'api/v1/auth/register/google/resend'
          : 'api/v1/auth/login/google/resend';
      const res = await firstValueFrom(
        this._dataService.post(path, {
          flowToken,
          continueUrl: this.passwordlessEmailContinueUrl(normalized),
        })
      );
      if (res?.flowToken) {
        this.entraEmailFlowToken = String(res.flowToken);
      }
      this._googleFirebaseAuth.storeEmailForSignInLink(normalized);
      return {
        email: normalized,
        phone: this.maskEmail(normalized),
        status: 'SMS',
      };
    } catch (err: unknown) {
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async startGoogleSignInViaBff(
    email: string,
    allowSignupFallback: boolean,
    passwordlessLink = false
  ): Promise<LoginResponse | ValidOTPResponse> {
    const normalized = email.trim().toLowerCase();
    const includeContinueUrl = this.shouldIncludePasswordlessContinueUrl(
      passwordlessLink
    );
    if (passwordlessLink) {
      this.googlePasswordlessLinkSession = true;
    }
    try {
      const res = await firstValueFrom(
        this._dataService.post('api/v1/auth/login/google/start', {
          email: normalized,
          continueUrl: includeContinueUrl
            ? this.passwordlessEmailContinueUrl(normalized)
            : undefined,
          ...this.bffSignupSourcePayload(),
        })
      );
      this.entraEmailFlowToken = String(res.flowToken || '');
      this.entraEmailFlowKind = 'login';
      this.effectiveAuthMode = 'login';
      if (includeContinueUrl) {
        this._googleFirebaseAuth.storeEmailForSignInLink(normalized);
      }
      return {
        email: normalized,
        phone: this.maskEmail(normalized),
        status: 'SMS',
      };
    } catch (err: unknown) {
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async startGoogleSignUpViaBff(
    email: string,
    allowLoginFallback: boolean,
    passwordlessLink = false
  ): Promise<LoginResponse | ValidOTPResponse> {
    const normalized = email.trim().toLowerCase();
    const includeContinueUrl = this.shouldIncludePasswordlessContinueUrl(
      passwordlessLink
    );
    if (passwordlessLink) {
      this.googlePasswordlessLinkSession = true;
    }
    try {
      const res = await firstValueFrom(
        this._dataService.post('api/v1/auth/register/google/start', {
          email: normalized,
          continueUrl: includeContinueUrl
            ? this.passwordlessEmailContinueUrl(normalized)
            : undefined,
          ...this.bffSignupSourcePayload(),
        })
      );
      this.entraEmailFlowToken = String(res.flowToken || '');
      this.entraEmailFlowKind = 'register';
      this.effectiveAuthMode = 'signup';
      if (includeContinueUrl) {
        this._googleFirebaseAuth.storeEmailForSignInLink(normalized);
      }
      return {
        email: normalized,
        phone: this.maskEmail(normalized),
        status: 'SMS',
      };
    } catch (err: unknown) {
      const message = this.apiErrorMessage(err);
      if (allowLoginFallback && this.isAccountsEmailTakenError(err, message)) {
        return this.startGoogleSignInViaBff(normalized, false);
      }
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async continueGoogleWithCodeViaBff(
    otp: string,
    email: string
  ): Promise<ValidOTPResponse | LoginResponse> {
    const flowToken = this.entraEmailFlowToken;
    const kind = this.entraEmailFlowKind;
    if (!flowToken || !kind) {
      throw { error: { message: 'Sign-in session expired. Please start again.' } };
    }

    const code = otp.trim();
    try {
      if (kind === 'login') {
        const res = await firstValueFrom(
          this._dataService.post('api/v1/auth/login/google/verify', {
            flowToken,
            code,
          })
        );
        return this.finishGoogleBffAuth(res, email);
      }

      const verifyRes = await firstValueFrom(
        this._dataService.post('api/v1/auth/register/google/verify', {
          flowToken,
          code,
        })
      );
      return this.completeGoogleRegisterViaBff(
        email,
        String(verifyRes.flowToken || flowToken || '')
      );
    } catch (err: unknown) {
      throw {
        error: { message: this.resolveApiError(err) },
        status: this.apiErrorStatus(err),
      };
    }
  }

  private async resendGoogleCodeViaBff(): Promise<LoginResponse> {
    const flowToken = this.entraEmailFlowToken;
    const kind = this.entraEmailFlowKind;
    if (!flowToken || !kind) {
      throw { error: { message: 'Sign-in session expired. Please start again.' } };
    }

    const path =
      kind === 'login'
        ? 'api/v1/auth/login/google/resend'
        : 'api/v1/auth/register/google/resend';
    const res = await firstValueFrom(
      this._dataService.post(path, { flowToken })
    );
    if (res.flowToken) {
      this.entraEmailFlowToken = String(res.flowToken);
    }
    return {
      email: '',
      phone: '',
      status: 'SMS',
    };
  }

  private async completeGoogleRegisterViaBff(
    email: string,
    flowToken?: string
  ): Promise<ValidOTPResponse> {
    const token = (flowToken || this.entraEmailFlowToken || '').trim();
    if (!token) {
      throw {
        error: {
          message: 'Registration session expired. Please start again.',
        },
      };
    }
    const names = this.resolveRegisterNames(email, '');
    const completeRes = await firstValueFrom(
      this._dataService.post('api/v1/auth/register/google/complete', {
        flowToken: token,
        firstName: names.firstName,
        lastName: names.lastName,
      })
    );
    return this.finishGoogleBffAuth(completeRes, email);
  }

  private async finishGoogleBffAuth(
    response: Record<string, unknown>,
    email: string
  ): Promise<ValidOTPResponse> {
    const customToken = String(response['firebaseCustomToken'] || '').trim();
    if (!customToken) {
      throw {
        error: {
          message: 'Sign-in succeeded but no Firebase session token was returned.',
        },
      };
    }

    const cred =
      await this._googleFirebaseAuth.signInWithCustomToken(customToken);
    const firebaseUser = cred.user;
    const { idToken, refreshToken } =
      await this._googleFirebaseAuth.resolveSessionTokens(firebaseUser);
    const resolvedEmail =
      email.trim().toLowerCase() ||
      firebaseUser.email?.trim().toLowerCase() ||
      '';

    this.registerSignUpAuthMethod = 'email';
    if (resolvedEmail && idToken) {
      try {
        await this.provisionAccountsUser(resolvedEmail, idToken);
      } catch (err) {
        console.warn('[auth] provisionAccountsUser after Google BFF OTP failed', err);
      }
    }

    this.clearEntraBffFlow();
    this.clearSignedOutLocally();
    this.clearFirebaseSocialSignInMarker();
    this.persistAuthData(resolvedEmail, {
      accessToken: idToken,
      refreshToken,
    });
    this.lastAccountsIdentity = response;
    this.upsertUserCache(resolvedEmail);

    const user = response['user'] as { id?: string } | undefined;
    return {
      status: 'Token',
      response: {
        accessToken: idToken,
        refreshToken,
      },
      userId: user?.id ? String(user.id) : '',
    };
  }
}

export interface AuthData {
  token: string;
  refreshToken: string;
  userName: string;
  expiresAt: Date;
}

export interface UserCache {
  userName: string;
}

export interface LoginResponse {
  phone: string;
  email: string;
  status:
    | 'Authenticator'
    | 'SMS'
    | 'Password'
    | 'Completed'
    | 'PhoneRegistration'
    | 'MfaCode';
  token?: AuthToken;
}

export interface AuthToken {
  accessToken: string;
  refreshToken: string;
  userType?: UserType;
}

export interface UserCompany {
  id: string;
  companyEmail: string;
  companyId: string;
  isPinned: boolean;
  isHidden: boolean;
  userId: string;
  company: {
    id: string;
    name: string;
  };
}

export interface CompanyProductLicense {
  subscriptionStatus: SubscriptionStatus;
  name: string;
  isEnabled: boolean;
  qty: number;
  totalAllowedBillingPhysicians: number;
  activeBillingPhysicians: number;
  purchaseChannel: PurchaseChannel;
  percentValue: number;
}

export interface AvailableLicenses {
  hasChartsLicense: boolean;
  hasStudiesLicense: boolean;
}

export interface ValidOTPResponse {
  status: 'Token' | 'Companies' | '2faRequired';
  response: AuthToken | UserCompany[];
  tempAccessToken?: string;
  qrCodeDataUrl?: string;
  userId: string;
}

export interface SmsMfaStatusResponse {
  enabled: boolean;
  groupId?: string;
  entraObjectId?: string;
  message?: string;
}

export interface CurrentUserResponse {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyId?: string | null;
  companyName?: string | null;
}

export interface SessionRefreshResponse {
  status: 'REFRESHED';
  token: string;
  refreshToken?: string;
  email?: string;
  expiresIn?: number;
  expiresAt?: string;
  tokenType?: string;
}

export interface EntraRefreshResponse {
  status?: string;
  tokens?: {
    accessToken?: string;
    refreshToken?: string;
    idToken?: string;
    expiresIn?: number;
    tokenType?: string;
  };
}

export enum SubscriptionStatus {
  Active = 1,
  Suspended,
}

export enum PurchaseChannel {
  CommercialDirect = 1,
  CommercialIndirect,
}

export enum UserType {
  BillingPhysician = 1,
  Nurse,
  MedicalAssistent,
  NonMedicalPersonal,
  Biller,
}
